// =============================================================================
// MHV Live Page — Socket.IO, viewer tracking, all lightboxes, product list
// Extracted from sections/live.liquid (Script Block 3)
//
// Reads config from window.MHV_LIVE_CONFIG (injected by live.liquid):
//   auctionDateCode, customer { id, firstName, lastName, screenName,
//   bidderNumber, consignorNumber, badge }
// Depends on: mhv-config.js (MHV.SERVER_URL, MHV.apiUrl, MHV.getDeviceInfo),
//             mhv-utils.js (MHV.escapeHtml),
//             live-main.js (isSlideshowVisible, window globals)
//             live-slideshow.js (slideshow display + navigation)
// =============================================================================

(function() {
  'use strict';

  // LOG-49: single source of truth for the "coming up" / placeholder-price
  // sentinel. Lots that have not been given a real price carry a sentinel
  // value that has historically appeared in several forms across the data:
  //   - the string '999999.99'
  //   - the float 999999.99 (or any value that parses to it)
  //   - the integer 99999999
  // This helper returns true for ALL of those forms so every call site agrees
  // on which lots are placeholders. (Strict superset of every prior inline
  // check — behavior-preserving; does NOT change cents-vs-dollars parsing.)
  function isPlaceholderPrice(price) {
    return (price === 99999999) ||
           (price === '999999.99') ||
           (parseFloat(price) === 999999.99);
  }

  var CONFIG = window.MHV_LIVE_CONFIG || {};
  var cust = CONFIG.customer;

  // Session code: locked session from server is source of truth.
  // sessionStorage caches the locked session to avoid reload loops.
  // CONFIG.auctionDateCode (Liquid metafield) is the initial fallback.
  var defaultSessionCode = CONFIG.auctionDateCode || '';
  var liveSessionCode = sessionStorage.getItem('mhv_live_session') || defaultSessionCode;

  // Fetch locked session from server and update if it differs
  function updateExitLink(code) {
    var exitLink = document.querySelector('.exit-link');
    if (exitLink && code) exitLink.href = '/collections/' + code;
  }
  // Set exit link immediately from cached session code
  if (liveSessionCode) updateExitLink(liveSessionCode);

  fetch(MHV.apiUrl('/api/sessions/locked'))
    .then(function(r) { return r.json(); })
    .then(function(data) {
      if (data.lockedSession) {
        sessionStorage.setItem('mhv_live_session', data.lockedSession);
        updateExitLink(data.lockedSession);
        if (data.lockedSession !== liveSessionCode) {
          // Locked session differs — reload to use the correct one
          location.reload();
        }
      }
    })
    .catch(function() { /* keep using current value */ });

  // Variables to manage lightbox state and history
  var viewerLightboxOpen = false;
  var previousHistoryState = null;

  // === Socket.IO Integration ===
  // Get customer data from config
  var customerData = {
    customerId: cust ? cust.id : null,
    isLoggedIn: cust !== null,
    firstName: cust ? cust.firstName : null,
    lastName: cust ? cust.lastName : null,
    screenName: cust ? (cust.screenName || '') : '',
    bidderNumber: cust ? (cust.bidderNumber || '') : '',
    badge: cust ? (cust.badge || '') : '',
    profileImage: cust ? (cust.profileImage || '') : '',
    deviceInfo: MHV.getDeviceInfo()
  };


  // Connect to Socket.IO server
  var socket = io(MHV.SERVER_URL, {
    reconnection: true,
    reconnectionAttempts: Infinity,
    reconnectionDelay: 1000,
    reconnectionDelayMax: 5000,
    transports: ['websocket', 'polling'],
    // WHICH WORLD THIS PAGE IS IN — decided at HANDSHAKE time, before any event
    // is sent. The id below is the same one already going out in `new-user`; the
    // difference is that this arrives in time to matter. For everyone except
    // staff it changes nothing at all: the server only reads it to decide
    // whether a rehearsal is visible, and a customer id it does not recognise as
    // staff leaves the connection in the real auction, which is the point.
    auth: function (cb) { cb({ customerId: (cust && cust.id) || null }); }
  });
  // Make socket globally available for chat widget to reuse
  window.socket = socket;

  // Staff watching along: a rehearsal starting or ending moves this page between
  // worlds, and that is fixed at handshake — so shake hands again.
  // ⚠️ Gated on the SERVER's answer to "are you staff?" (published by
  // mhv-sim-notice.js), not on being logged in. Most people watching a live sale
  // are logged in — bidding requires it — so reconnecting all of them would hand
  // the whole audience a blip every time someone starts a rehearsal, for
  // something they will never see.
  ['simulation-started', 'simulation-ended'].forEach(function (evt) {
    socket.on(evt, function () {
      if (!window.__mhvSimStaff) return;
      try { socket.disconnect(); socket.connect(); } catch (e) { /* reconnect logic will retry */ }
    });
  });

  // --- Live connection status banner ---------------------------------------
  // A fixed top bar so customers know when the live auction connection drops
  // (it affects bids + lot updates), and that it's auto-recovering — instead of
  // silently sitting on a dead socket. The socket already reconnects forever and
  // re-joins on every 'connect', so this is purely the visible signal.
  var liveStatusBar = document.createElement('div');
  liveStatusBar.id = 'mhv-live-status';
  liveStatusBar.setAttribute('role', 'status');
  liveStatusBar.style.cssText = 'position:fixed;top:0;left:0;right:0;z-index:99999;display:none;padding:8px 12px;font-size:13px;font-weight:700;text-align:center;background:#8a6d00;color:#fff;box-shadow:0 2px 8px rgba(0,0,0,.35);';
  liveStatusBar.textContent = '⚠️ Reconnecting to the live auction…';
  function mountLiveStatusBar() { if (!liveStatusBar.parentNode && document.body) document.body.appendChild(liveStatusBar); }
  mountLiveStatusBar();
  if (!liveStatusBar.parentNode) document.addEventListener('DOMContentLoaded', mountLiveStatusBar);
  var liveConnected = true;
  function setLiveConnected(connected) {
    liveConnected = connected;
    mountLiveStatusBar();
    liveStatusBar.style.display = connected ? 'none' : 'block';
    if (!connected) liveStatusBar.textContent = '⚠️ Reconnecting to the live auction…';
  }
  // v4 reconnect events live on the manager (socket.io), not the socket.
  socket.io.on('reconnect_attempt', function(n) {
    if (!liveConnected) liveStatusBar.textContent = '⚠️ Reconnecting to the live auction… (attempt ' + n + ')';
  });

  // Get DOM elements for updating the widget
  var viewerCountEl = document.querySelector('.viewer-count');
  var viewerListEl = document.getElementById('viewer-list');

  // Connect to Socket.IO server
  socket.on('connect', function() {
    // Fires on EVERY (re)connection in socket.io v4 — clears the banner and
    // re-joins so the live page recovers automatically after a drop.
    setLiveConnected(true);

    // Send user data to server
    socket.emit('new-user', customerData);

    // Request visibility state from server
    socket.emit('request-visibility-state');
  });

  // Handle heartbeat from server
  socket.on('heartbeat', function() {
    socket.emit('heartbeat-response');
  });

  // Handle viewer updates
  socket.on('viewer-update', function(data) {
    if (viewerCountEl) {
      viewerCountEl.textContent = data.count;
    }

    // Update viewer list if lightbox is open
    if (viewerListEl && document.getElementById('viewer-lightbox').style.display === 'flex') {
      // Sort viewers alphabetically for live page (case-insensitive)
      var sortedViewers = [].concat(data.viewers).sort(function(a, b) {
        // Handle both old format (strings) and new format (objects)
        var nameA = typeof a === 'string' ? a : a.name;
        var nameB = typeof b === 'string' ? b : b.name;
        return nameA.toLowerCase().localeCompare(nameB.toLowerCase());
      });

      // Update logged-in users list
      var viewerHTML = sortedViewers.map(function(viewer) {
        if (typeof viewer === 'string') {
          return '<li><span style="color: var(--mhv-text-primary); font-weight: bold;">' + MHV.escapeHtml(viewer) + '</span></li>';
        } else {
          // Resolve display name (live page = public/non-admin view)
          var disp = MHV.chatDisplayName({
            screenName: viewer.name, firstName: viewer.firstName, lastName: viewer.lastName,
            bidderNumber: viewer.bidderNumber, hideName: viewer.hideName, isAdminView: false
          });
          // Primary name in white ("Bidder" word for the bidder fallback — the pill shows the number)
          var nameText = disp.bidderFallback ? 'Bidder' : disp.primary;
          var nameDisplay = '<span style="color: var(--mhv-text-primary); font-weight: bold;">' + MHV.escapeHtml(nameText) + '</span>';
          // Badge if present
          if (viewer.badge) {
            nameDisplay += '<span style="font-size: 14px; margin-left: 4px;">' + MHV.escapeHtml(viewer.badge) + '</span>';
          }
          // Bidder number in blue pill
          if (viewer.bidderNumber) {
            nameDisplay += '<span style="background: var(--mhv-info-blue); color: var(--mhv-text-primary); font-size: 12px; font-weight: bold; padding: 1px 5px; border-radius: 4px; margin-left: 4px;">' + MHV.escapeHtml(viewer.bidderNumber) + '</span>';
          }
          // Real name (First L.) under a screen name when revealed
          if (disp.secondary) {
            nameDisplay += '<span style="color: var(--mhv-chat-admin-name); font-size: 12px; font-weight: bold; margin-left: 6px;">' + MHV.escapeHtml(disp.secondary) + '</span>';
          }
          // Profile data lives only in quote-escaped data-* attributes; the
          // onclick is static JS that reads them (no interpolated user data) —
          // closes the screen-name attribute-breakout XSS (SEC-61).
          // Keyed on BIDDER NUMBER, not the Shopify customer id — the roster broadcast
          // no longer carries customer ids to public sockets at all (Q27/Q28). The
          // server answers /api/public-profile/bidder/:n with the identical
          // public-safe shape.
          var clickAttr = viewer.bidderNumber ? ' data-bidder="' + MHV.escapeAttr(viewer.bidderNumber) + '" data-profile="' + MHV.escapeAttr(JSON.stringify({ screenName: disp.primary || '', profileImage: viewer.profileImage || '', badge: viewer.badge || '', bidderNumber: viewer.bidderNumber || '' })) + '" onclick="openProfileLightboxByBidder(this.dataset.bidder, JSON.parse(this.dataset.profile||\'{}\'))" style="cursor:pointer;"' : '';
          return '<li' + clickAttr + '>' + nameDisplay + '</li>';
        }
      }).join('');
      viewerListEl.innerHTML = viewerHTML;

      // Update guest count
      var guestCountEl = document.getElementById('guest-count');
      if (guestCountEl) {
        guestCountEl.textContent = data.guestCount + ' Guest' + (data.guestCount !== 1 ? 's' : '');
      }
    }
  });

  // Handle real-time collection updates
  socket.on('collection-updated', function(data) {
    if (CONFIG.auctionDateCode) {
      if (data.handle === liveSessionCode) {
        // Update our cached product data silently
        allProducts = data.data.products;
        // Re-evaluate request list button visibility (hide if everything is sold)
        updateRequestListButtonVisibility(allProducts);
      }
    }
  });

  // Function to request current slideshow state from chat page
  function requestSlideshowState() {
    if (socket && socket.connected) {
      socket.emit('request-slideshow-state');

      // Set a timeout in case chat page doesn't respond
      setTimeout(function() {
      }, 5000); // 5 second timeout
    }
  }
  window.requestSlideshowState = requestSlideshowState;

  // Handle slideshow state response from chat page
  socket.on('slideshow-state-response', function(data) {
    if (data && typeof data.visible === 'boolean') {
      // Update local state to match chat
      window.isSlideshowVisible = data.visible;
      localStorage.setItem('chatSlidesSlideshowVisible', data.visible.toString());

      // Apply the visibility state if it's different from current
      if (data.visible !== (document.getElementById('product-slideshow-container').style.display !== 'none')) {
        if (data.visible) {
          // Don't call showSlideshow() here as it will be called when product loads
        } else {
          toggleSlideshowVisibility(false);
        }
      }
    }
  });

  // Handle visibility state response from server (slideshow only - toolbar always shows)
  socket.on('visibility-state-response', function(data) {
    // Apply slideshow visibility
    if (typeof data.slideshow === 'boolean') {
      window.isSlideshowVisible = data.slideshow;
      localStorage.setItem('chatSlidesSlideshowVisible', data.slideshow.toString());
      toggleSlideshowVisibility(data.slideshow);
    }
  });

  // Handle slideshow updates from server
  socket.on('current-lot-updated', function(data) {
    // A single-lot update supersedes any active multi-lot grid
    if (window.currentGridLots && typeof window.clearLiveGrid === 'function') {
      window.clearLiveGrid();
    }

    // Check if this is a clear action
    if (data.sku === 'CLEARED' && data.action === 'clear') {
      clearSlideshow(true); // Pass true to indicate this is a full clear (hide title and info button)

      // Hide the Gallery button since product is cleared
      var galleryBtns = document.querySelectorAll('.photo-gallery-btn');
      galleryBtns.forEach(function(btn) { btn.classList.remove('has-content'); });

      return;
    }

    // Check if this is a slideshow visibility toggle (legacy)
    if (data.sku === 'SLIDESHOW-TOGGLE' && data.action === 'toggle-slideshow') {
      // Update local state to match chat
      window.isSlideshowVisible = data.visible;
      localStorage.setItem('chatSlidesSlideshowVisible', data.visible.toString());

      // Apply the visibility state
      if (data.visible) {
        // Don't call showSlideshow() here as it will be called when product loads
      } else {
        toggleSlideshowVisibility(false);
      }
      return;
    }

    // Check if this is a host visibility toggle (element encoded in SKU)
    // Only slideshow visibility is controllable - toolbar always shows to viewers
    if (data.sku && data.sku.startsWith('VISIBILITY-TOGGLE-') && data.action === 'visibility-toggle') {
      var element = data.sku.replace('VISIBILITY-TOGGLE-', '');
      var visible = data.visible;

      if (element === 'slideshow') {
        // Toggle slideshow visibility
        window.isSlideshowVisible = visible;
        localStorage.setItem('chatSlidesSlideshowVisible', visible.toString());
        toggleSlideshowVisibility(visible);
      }
      return;
    }

    // Enhanced validation for product data
    if (!data.product) {
      console.error('No product data received from server');
      return;
    }

    // Track if this is auction terms mode
    window.isAuctionTermsSlideshow = (data.sku === 'AUCTION-TERMS' || data.action === 'auction-terms');

    var images = data.product.images || [];

    // Special handling for photo gallery updates
    if (data.sku === 'PHOTO-GALLERY') {
      if (images.length === 0) {
        console.warn('Photo gallery has no images to display');
      }
    }

    // Special handling for auction terms - don't auto-advance
    if (window.isAuctionTermsSlideshow) {
      // Store the slide index to apply after slideshow is created
      window.auctionTermsInitialSlide = data.slideIndex || 0;
    }

    // Always show slideshow for product updates, even if no images
    // This ensures the product title display is properly shown
    try {
      showSlideshow(images, data.product, window.isAuctionTermsSlideshow);

      // Navigate to the correct slide for late joiners (works for auction terms and regular lots)
      var initialSlide = window.isAuctionTermsSlideshow ? (data.slideIndex || 0) : (data.slideIndex || 0);
      if (typeof initialSlide === 'number' && initialSlide > 0) {
        var slides = document.querySelectorAll('#product-slideshow .slide');
        if (slides.length > initialSlide) {
          slides.forEach(function(slide, i) {
            slide.classList.toggle('active', i === initialSlide);
          });
          window.currentSlideIndex = initialSlide;
        }
      }

      // Update the photo gallery lightbox if it's open
      if (typeof photoGalleryLightboxOpen !== 'undefined' && photoGalleryLightboxOpen) {
        setTimeout(function() {
          if (typeof loadPhotoGalleryFromCurrentProduct === 'function') {
            loadPhotoGalleryFromCurrentProduct();
          }
        }, 300); // Small delay to ensure slideshow has updated
      }
    } catch (error) {
      console.error('Error displaying slideshow on live page:', error);

      // Try again after a short delay
      setTimeout(function() {
        try {
          showSlideshow(images, data.product);
        } catch (retryError) {
          console.error('Slideshow display failed on retry:', retryError);
        }
      }, 1000);
    }
  });

  // ========================================
  // MULTI-LOT GRID — live display for sell-as-group / on-choice
  // When the auctioneer pushes a grid from the selling widget, viewers see a
  // tiled layout of the candidate lots instead of the single-lot slideshow.
  // ========================================
  window.currentGridLots = null;
  window.currentGridLayout = null;

  function renderLiveGrid(lots, layout, hero, chosenKeys, committedKeys) {
    var gridContainer = document.getElementById('product-grid-container');
    var slideshowContainer = document.getElementById('product-slideshow-container');
    if (!gridContainer) return;

    var cappedLots = Array.isArray(lots) ? lots.slice(0, 6) : [];
    var count = Math.max(1, Math.min(6, cappedLots.length));
    var mode = String(layout || count);
    var chosenSet = {}; (chosenKeys || []).forEach(function (k) { chosenSet[String(k)] = 1; });
    var committedSet = {}; (committedKeys || []).forEach(function (k) { committedSet[String(k)] = 1; });

    // Grid supersedes single-lot slideshow: hide it + stop autoplay so it
    // doesn't keep cycling under the grid.
    if (slideshowContainer) slideshowContainer.style.display = 'none';
    if (window.slideshowInterval) {
      clearInterval(window.slideshowInterval);
      window.slideshowInterval = null;
    }

    gridContainer.className = 'product-grid-container mode-grid-' + mode + (hero ? ' has-hero' : '');
    gridContainer.style.display = 'grid';

    var slots = gridContainer.querySelectorAll('.grid-slot');
    slots.forEach(function(slot) {
      var slotIdx = parseInt(slot.getAttribute('data-slot'), 10) - 1;
      var img = slot.querySelector('.grid-slot-img');
      var title = slot.querySelector('.grid-slot-title');
      var lot = cappedLots[slotIdx];
      slot.classList.remove('empty');
      slot.classList.remove('blank');
      slot.classList.remove('chosen');
      slot.classList.remove('committed');
      if (!lot) {
        if ((mode === '3' && slot.dataset.slot === '4') || (mode === '5' && slot.dataset.slot === '6')) {
          slot.classList.add('blank');
        } else {
          slot.classList.add('empty');
        }
        if (img) { img.removeAttribute('src'); img.alt = ''; }
        if (title) title.textContent = '';
        return;
      }
      // On Choice: green = picked by the winning bidder, gray = already sold.
      if (lot.choiceKey && committedSet[String(lot.choiceKey)]) slot.classList.add('committed');
      else if (lot.choiceKey && chosenSet[String(lot.choiceKey)]) slot.classList.add('chosen');
      var firstImage = (lot.product && Array.isArray(lot.product.images) && lot.product.images[0]) || null;
      var imgUrl = firstImage ? (firstImage.url || firstImage.src || '') : '';
      if (img) {
        if (imgUrl) {
          img.src = imgUrl.indexOf('?') >= 0 ? imgUrl + '&width=800' : imgUrl + '?width=800';
          img.alt = (lot.product && lot.product.title) || lot.sku || '';
        } else {
          img.removeAttribute('src');
          img.alt = '';
        }
      }
      if (title) {
        title.textContent = (lot.product && lot.product.title) || lot.sku || '';
      }
    });

    window.currentGridLots = cappedLots;
    window.currentGridLayout = mode;
  }

  function clearLiveGrid() {
    var gridContainer = document.getElementById('product-grid-container');
    var slideshowContainer = document.getElementById('product-slideshow-container');
    if (gridContainer) {
      gridContainer.style.display = 'none';
      gridContainer.className = 'product-grid-container';
      var slots = gridContainer.querySelectorAll('.grid-slot');
      slots.forEach(function(slot) {
        var img = slot.querySelector('.grid-slot-img');
        var title = slot.querySelector('.grid-slot-title');
        if (img) { img.removeAttribute('src'); img.alt = ''; }
        if (title) title.textContent = '';
        slot.classList.remove('empty');
        slot.classList.remove('blank');
      });
    }
    if (slideshowContainer) slideshowContainer.style.display = '';
    window.currentGridLots = null;
    window.currentGridLayout = null;
  }

  window.renderLiveGrid = renderLiveGrid;
  window.clearLiveGrid = clearLiveGrid;

  socket.on('current-lot-grid-updated', function(data) {
    if (!data || data.mode === 'clear' || !Array.isArray(data.lots) || data.lots.length === 0) {
      clearLiveGrid();
      return;
    }
    try {
      renderLiveGrid(data.lots, data.layout, data.hero, data.chosenKeys, data.committedKeys);
    } catch (e) {
      console.error('Error rendering live grid:', e);
    }
  });

  // ========================================
  // SLIDESHOW SYNC — follow owner's slide changes
  // ========================================
  socket.on('slideshow-slide-changed', function(data) {
    var slides = document.querySelectorAll('.photo-slideshow .slide');
    if (slides.length === 0) return;

    // Stop any local autoplay that might be running
    if (window.slideshowInterval) {
      clearInterval(window.slideshowInterval);
      window.slideshowInterval = null;
    }

    // Apply the slide from the owner
    slides.forEach(function(s) { s.classList.remove('active'); });
    if (slides[data.slideIndex]) slides[data.slideIndex].classList.add('active');
    window.currentSlideIndex = data.slideIndex;
    window.hasCompletedCycle = data.hasCompletedCycle;

    // Update dots if they exist
    var dots = document.querySelectorAll('.slideshow-dot');
    dots.forEach(function(dot, i) {
      dot.classList.toggle('active', i === data.slideIndex);
    });

  });

  // ========================================
  // AUCTION TERMS STATE HANDLER (Server-synced)
  // ========================================

  // Track auction terms mode state
  window.auctionTermsActive = false;

  // Handle auction terms state updates from server
  socket.on('auction-terms-state-update', function(data) {
    window.auctionTermsActive = data.enabled;
    window.isAuctionTermsSlideshow = data.enabled;

    if (!data.enabled) {
      // Slideshow will be updated via set-current-lot when host loads new content
    }
  });

  // Handle auction terms slide updates from server
  socket.on('auction-terms-slide-update', function(data) {
    // Stop any auto-advance regardless of mode
    if (window.slideshowInterval) {
      clearInterval(window.slideshowInterval);
      window.slideshowInterval = null;
    }

    // Navigate to the slide - use correct selector for live page slideshow
    var slides = document.querySelectorAll('#product-slideshow .slide');

    if (slides.length > 0 && typeof data.slideIndex === 'number') {
      slides.forEach(function(slide, i) {
        slide.classList.toggle('active', i === data.slideIndex);
      });
      window.currentSlide = data.slideIndex;
    }
  });

  // Request auction terms state on connect
  socket.on('connect', function() {
    socket.emit('auction-terms-request-state');
  });

  // Also request immediately if already connected
  if (socket.connected) {
    socket.emit('auction-terms-request-state');
  }

  // ========================================
  // END AUCTION TERMS STATE HANDLER
  // ========================================

  // ========================================
  // Viewer Lightbox Functions
  // ========================================
  window.openViewerLightbox = function() {
    if (customerData.isLoggedIn) {
      var lightbox = document.getElementById('viewer-lightbox');
      lightbox.style.display = 'flex';
      viewerLightboxOpen = true;
      previousHistoryState = window.history.state;
      // Push a new state so that the Android back button can close the lightbox
      window.history.pushState({ viewerLightboxOpen: true }, '');
      socket.emit('request-viewer-list');
    }
  };

  window.closeViewerLightbox = function() {
    var lightbox = document.getElementById('viewer-lightbox');
    lightbox.style.display = 'none';
    viewerLightboxOpen = false;
    // Restore the previous history state
    window.history.replaceState(previousHistoryState, '');
  };

  window.closeViewerLightboxOnOutsideClick = function(event) {
    if (event.target === document.getElementById('viewer-lightbox')) {
      closeViewerLightbox();
    }
  };

  // ========================================
  // Product List Lightbox Functions
  // ========================================
  var currentTab = 'coming-up';
  var allProducts = [];
  var liveProductSearchFilter = ''; // Current search filter text

  // Customer bidder number for filtering bids and favs
  var customerBidderNumber = (cust && cust.bidderNumber) ? cust.bidderNumber : null;
  var customerFavTag = customerBidderNumber ? customerBidderNumber + '-favs' : null;

  // Check if any products in the list are unsold (requestable)
  function hasUnsoldProducts(products) {
    if (!products || products.length === 0) return false;
    return products.some(function(product) {
      var firstVariant = product.variants && product.variants[0];
      var price = firstVariant ? firstVariant.price : 0;
      var tags = product.tags || [];
      var hasPassed = tags.some(function(tag) { return tag.toLowerCase().includes('passed'); });
      var isComingUpPrice = isPlaceholderPrice(price);
      return isComingUpPrice && !hasPassed;
    });
  }

  // Show or hide request list buttons based on unsold item availability
  function updateRequestListButtonVisibility(products) {
    var requestListBtns = document.querySelectorAll('.request-list-btn');
    if (hasUnsoldProducts(products)) {
      requestListBtns.forEach(function(btn) { btn.classList.add('has-content'); });
    } else {
      requestListBtns.forEach(function(btn) { btn.classList.remove('has-content'); });
    }
  }

  // Filter product list by search term (title only)
  window.filterLiveProductList = function(searchTerm) {
    liveProductSearchFilter = searchTerm || '';

    // Update clear button visibility
    var clearBtn = document.getElementById('live-product-search-clear');
    if (clearBtn) {
      if (liveProductSearchFilter.trim()) {
        clearBtn.classList.add('visible');
      } else {
        clearBtn.classList.remove('visible');
      }
    }

    // Re-render with filter applied
    if (allProducts.length > 0) {
      displayProductList(allProducts);
    }
  };

  // Clear product list search
  window.clearLiveProductSearch = function() {
    liveProductSearchFilter = '';

    var searchInput = document.getElementById('live-product-search-input');
    var clearBtn = document.getElementById('live-product-search-clear');

    if (searchInput) {
      searchInput.value = '';
      searchInput.focus();
    }
    if (clearBtn) {
      clearBtn.classList.remove('visible');
    }

    // Re-render without filter
    if (allProducts.length > 0) {
      displayProductList(allProducts);
    }
  };

  // ========================================
  // Request List Visibility (legacy stub for backwards compatibility)
  // ========================================

  // Keep hideRequestTooltip as a no-op for backwards compatibility
  window.hideRequestTooltip = function() {
    // No longer needed - tooltip removed, Request List button now in chat toolbar
  };

  // ========================================
  // End Request List
  // ========================================

  window.openProductListLightbox = function() {
    var lightbox = document.getElementById('product-list-lightbox');
    lightbox.style.display = 'flex';

    // Reset to coming up tab
    currentTab = 'coming-up';
    switchTab('coming-up');

    // If we have cached data, display it immediately, then refresh
    if (allProducts && allProducts.length > 0) {
      displayProductList(allProducts);
    }

    // Always load fresh data when opening
    loadProductList();
  };

  window.switchTab = function(tab) {
    currentTab = tab;

    // Update tab styling - reset all tabs first
    var allTabs = ['coming-up-tab', 'my-bids-tab', 'my-favs-tab', 'sold-tab'];
    allTabs.forEach(function(tabId) {
      var tabEl = document.getElementById(tabId);
      if (tabEl) {
        tabEl.style.color = 'var(--mhv-text-subtle)';
        tabEl.style.borderBottomColor = 'transparent';
        tabEl.style.fontWeight = 'bold';
      }
    });

    // Highlight active tab
    var activeTabEl = document.getElementById(tab + '-tab');
    if (activeTabEl) {
      activeTabEl.style.color = 'var(--mhv-text-primary)';
      activeTabEl.style.borderBottomColor = 'var(--mhv-accent)';
      activeTabEl.style.fontWeight = 'bold';
    }

    // Filter and display products if we have them
    if (allProducts.length > 0) {
      displayProductList(allProducts);
    }
  };

  window.closeProductListLightbox = function() {
    var lightbox = document.getElementById('product-list-lightbox');
    lightbox.style.display = 'none';
  };

  window.closeProductListLightboxOnOutsideClick = function(event) {
    if (event.target === document.getElementById('product-list-lightbox')) {
      closeProductListLightbox();
    }
  };

  // ========================================
  // BIN List Lightbox Functions
  // ========================================
  var binListItems = []; // Cache of BIN items for display
  window.binListItems = binListItems; // Expose for chat widget updates
  var binListSearchFilter = '';

  window.openBinListLightbox = function() {
    var lightbox = document.getElementById('bin-list-lightbox');
    if (!lightbox) return;

    lightbox.style.display = 'flex';

    // Clear search
    var searchInput = document.getElementById('bin-list-search-input');
    if (searchInput) {
      searchInput.value = '';
      binListSearchFilter = '';
    }

    // Render the list
    renderBinList();
  };

  window.closeBinListLightbox = function() {
    var lightbox = document.getElementById('bin-list-lightbox');
    if (lightbox) {
      lightbox.style.display = 'none';
    }
  };

  window.closeBinListLightboxOnOutsideClick = function(event) {
    if (event.target === document.getElementById('bin-list-lightbox')) {
      closeBinListLightbox();
    }
  };

  window.filterBinList = function(searchTerm) {
    binListSearchFilter = searchTerm.toLowerCase().trim();
    renderBinList();
  };

  window.clearBinListSearch = function() {
    var searchInput = document.getElementById('bin-list-search-input');
    if (searchInput) {
      searchInput.value = '';
    }
    binListSearchFilter = '';
    renderBinList();
  };

  window.updateBinListItems = function(items) {
    // Filter to only show active items (not sold or passed)
    binListItems = (items || []).filter(function(item) {
      return item.status === 'active' || item.status === undefined || item.status === null;
    });
    window.binListItems = binListItems; // Keep window reference in sync

    // Update button visibility
    var binListBtns = document.querySelectorAll('.bin-list-btn');
    binListBtns.forEach(function(btn) {
      if (binListItems.length > 0) {
        btn.classList.add('has-content');
      } else {
        btn.classList.remove('has-content');
      }
    });

    // Re-render if lightbox is open
    var lightbox = document.getElementById('bin-list-lightbox');
    if (lightbox && lightbox.style.display === 'flex') {
      renderBinList();
    }
  };

  // Remove a specific BIN item from the list (when sold/passed)
  window.removeBinListItem = function(binId) {
    binListItems = binListItems.filter(function(item) { return item.id !== binId; });
    window.binListItems = binListItems;

    // Update button visibility
    var binListBtns = document.querySelectorAll('.bin-list-btn');
    binListBtns.forEach(function(btn) {
      if (binListItems.length > 0) {
        btn.classList.add('has-content');
      } else {
        btn.classList.remove('has-content');
      }
    });

    // Re-render if lightbox is open
    var lightbox = document.getElementById('bin-list-lightbox');
    if (lightbox && lightbox.style.display === 'flex') {
      renderBinList();
    }
  };

  function renderBinList() {
    var container = document.getElementById('bin-list-content');
    var emptyEl = document.getElementById('bin-list-empty');

    if (!container) return;

    // Filter items by search term
    var filteredItems = binListItems;
    if (binListSearchFilter) {
      filteredItems = binListItems.filter(function(item) {
        return (item.title || '').toLowerCase().includes(binListSearchFilter);
      });
    }

    // Show empty state if no items
    if (filteredItems.length === 0) {
      container.innerHTML = '';
      if (emptyEl) {
        emptyEl.style.display = 'block';
        if (binListSearchFilter) {
          emptyEl.innerHTML = '<div>No matching BIN items found</div>';
        } else {
          emptyEl.innerHTML = '<div>No Buy-It-Now items available</div>';
        }
      }
      return;
    }

    if (emptyEl) emptyEl.style.display = 'none';

    // Check if user is logged in (has customer ID)
    var chatContainer = document.getElementById('custom-chat-container');
    var isLoggedIn = chatContainer && chatContainer.dataset.customerId;

    // Render items
    container.innerHTML = filteredItems.map(function(item) {
      var price = parseFloat(item.price) || 0;
      var formattedPrice = '$' + price.toFixed(2);
      var title = item.title || 'Untitled Item';

      // Only show purchase button if logged in
      var purchaseBtn = isLoggedIn ? '\
        <button class="bin-list-purchase-btn" \
                data-act="bin-list-purchase" \
                data-bin-id="' + MHV.escapeAttr(item.id) + '" \
                data-p0="' + MHV.escapeAttr(item.id) + '" \
                data-p1="' + price + '" \
                data-price="' + MHV.escapeAttr(price) + '">\
          BIN ' + formattedPrice + '\
        </button>\
      ' : '';

      return '\
        <div class="bin-list-item" data-bin-list-id="' + MHV.escapeAttr(item.id) + '">\
          <div class="bin-list-item-info" data-act="scroll-to-bin-item" data-p0="' + MHV.escapeAttr(item.id) + '">\
            <div class="bin-list-item-title">' + MHV.escapeHtml(title) + '</div>\
            <div class="bin-list-item-price">' + formattedPrice + '</div>\
          </div>\
          ' + purchaseBtn + '\
        </div>\
      ';
    }).join('');
  }

  // Track confirming state for BIN list buttons
  window.binListConfirmTimeouts = {};

  // Handle BIN purchase from the list
  window.binListPurchase = function(binId, price, btn) {
    if (!btn || btn.disabled || btn.classList.contains('sold')) return;

    var sock = window.socket;
    if (!sock || !sock.connected) {
      console.warn('Socket not connected');
      return;
    }

    var isConfirming = btn.dataset.confirming === 'true';
    var isPurchaseImmediate = getBinListPurchaseSetting() === 'immediate';

    if (isConfirming) {
      // Second click - confirm purchase
      btn.innerHTML = '<span class="loading-ellipsis">Claiming</span>';
      btn.disabled = true;
      btn.classList.add('loading');

      // Clear timeout
      if (window.binListConfirmTimeouts[binId]) {
        clearTimeout(window.binListConfirmTimeouts[binId]);
        delete window.binListConfirmTimeouts[binId];
      }

      sock.emit('bin-confirm-purchase', { binId: binId });
    } else if (isPurchaseImmediate) {
      // Purchase Immediately mode - skip confirmation
      btn.innerHTML = '<span class="loading-ellipsis">Claiming</span>';
      btn.disabled = true;
      btn.classList.add('loading');

      sock.emit('bin-confirm-purchase', { binId: binId });
    } else {
      // First click - show confirmation
      btn.classList.add('confirming');
      btn.dataset.confirming = 'true';
      btn.textContent = 'CONFIRM $' + price.toFixed(2);

      // Request confirmation from server (updates chat as well)
      sock.emit('bin-purchase', { binId: binId });

      // Set 5-second timeout to reset button
      window.binListConfirmTimeouts[binId] = setTimeout(function() {
        if (btn.dataset.confirming === 'true' && !btn.classList.contains('sold')) {
          btn.classList.remove('confirming');
          btn.dataset.confirming = 'false';
          btn.textContent = 'BIN $' + price.toFixed(2);
        }
      }, 5000);
    }
  };

  // Get BIN purchase setting from localStorage
  function getBinListPurchaseSetting() {
    try {
      return localStorage.getItem('mhv_bin_purchase_mode') || 'confirm';
    } catch (e) {
      return 'confirm';
    }
  }

  // Update a BIN list item's button state (called from chat widget socket handlers)
  window.updateBinListItemState = function(binId, state) {
    var btn = document.querySelector('.bin-list-purchase-btn[data-bin-id="' + binId + '"]');
    if (!btn) return;

    if (state === 'confirming') {
      var price = parseFloat(btn.dataset.price) || 0;
      btn.classList.add('confirming');
      btn.dataset.confirming = 'true';
      btn.textContent = 'CONFIRM $' + price.toFixed(2);

      // Set timeout to reset
      if (window.binListConfirmTimeouts[binId]) {
        clearTimeout(window.binListConfirmTimeouts[binId]);
      }
      window.binListConfirmTimeouts[binId] = setTimeout(function() {
        if (btn.dataset.confirming === 'true' && !btn.classList.contains('sold')) {
          btn.classList.remove('confirming');
          btn.dataset.confirming = 'false';
          btn.textContent = 'BIN $' + price.toFixed(2);
        }
      }, 5000);
    } else if (state === 'sold') {
      btn.classList.remove('confirming');
      btn.classList.add('sold');
      btn.textContent = 'SOLD';
      btn.disabled = true;
      if (window.binListConfirmTimeouts[binId]) {
        clearTimeout(window.binListConfirmTimeouts[binId]);
        delete window.binListConfirmTimeouts[binId];
      }
    } else if (state === 'reset') {
      var resetPrice = parseFloat(btn.dataset.price) || 0;
      btn.classList.remove('confirming', 'loading');
      btn.dataset.confirming = 'false';
      btn.disabled = false;
      btn.textContent = 'BIN $' + resetPrice.toFixed(2);
      if (window.binListConfirmTimeouts[binId]) {
        clearTimeout(window.binListConfirmTimeouts[binId]);
        delete window.binListConfirmTimeouts[binId];
      }
    }
  };

  window.scrollToBinItem = function(binId) {
    // Close the lightbox
    closeBinListLightbox();

    // Find the BIN item in chat
    var binEl = document.querySelector('[data-bin-id="' + binId + '"]');
    if (binEl) {
      binEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
      // Briefly highlight the item
      binEl.style.transition = 'box-shadow 0.3s';
      binEl.style.boxShadow = '0 0 20px rgba(255, 215, 0, 0.8)';
      setTimeout(function() {
        binEl.style.boxShadow = '';
      }, 1500);
    }
  };

  // ========================================
  // Photo Gallery Lightbox Functions
  // ========================================
  var photoGalleryImages = [];
  var photoGalleryCurrentIndex = 0;
  var photoGalleryLightboxOpen = false;
  var photoGalleryPreviousHistoryState = null;
  var currentGalleryProduct = null;

  window.openPhotoGalleryLightbox = function() {
    var lightbox = document.getElementById('photo-gallery-lightbox');
    if (!lightbox) return;

    lightbox.style.display = 'flex';
    photoGalleryLightboxOpen = true;
    photoGalleryPreviousHistoryState = window.history.state;
    window.history.pushState({ photoGalleryLightboxOpen: true }, '');

    // Load current product images if available
    loadPhotoGalleryFromCurrentProduct();
  };

  window.closePhotoGalleryLightbox = function() {
    var lightbox = document.getElementById('photo-gallery-lightbox');
    if (!lightbox) return;

    lightbox.style.display = 'none';
    photoGalleryLightboxOpen = false;
    window.history.replaceState(photoGalleryPreviousHistoryState, '');
  };

  window.closePhotoGalleryLightboxOnOutsideClick = function(event) {
    if (event.target === document.getElementById('photo-gallery-lightbox')) {
      closePhotoGalleryLightbox();
    }
  };

  // Load images from the currently showing product
  function loadPhotoGalleryFromCurrentProduct() {
    var mainImage = document.getElementById('photo-gallery-main-image');
    var emptyState = document.getElementById('photo-gallery-empty');
    var thumbnailsContainer = document.getElementById('photo-gallery-thumbnails');
    var titleEl = document.getElementById('photo-gallery-title');
    var counterEl = document.getElementById('photo-gallery-counter');
    var prevBtn = document.getElementById('photo-gallery-prev');
    var nextBtn = document.getElementById('photo-gallery-next');

    // Try to get images from the current slideshow
    var slideshowSlides = document.querySelectorAll('.photo-slideshow .slide');

    if (slideshowSlides.length > 0) {
      // Extract images from slideshow
      photoGalleryImages = Array.from(slideshowSlides).map(function(slide, index) {
        return {
          url: slide.src,
          alt: slide.alt || ('Image ' + (index + 1))
        };
      });

      // Get product title from the slideshow title element
      var slideshowTitle = document.getElementById('slideshow-title');
      if (slideshowTitle && titleEl) {
        titleEl.textContent = slideshowTitle.textContent || 'Photo Gallery';
      }
    } else {
      photoGalleryImages = [];
    }

    if (photoGalleryImages.length === 0) {
      // Show empty state
      if (mainImage) mainImage.style.display = 'none';
      if (emptyState) emptyState.style.display = 'block';
      if (thumbnailsContainer) thumbnailsContainer.innerHTML = '';
      if (counterEl) counterEl.textContent = '0 / 0';
      if (prevBtn) prevBtn.style.display = 'none';
      if (nextBtn) nextBtn.style.display = 'none';
      return;
    }

    // Show images
    if (mainImage) mainImage.style.display = 'block';
    if (emptyState) emptyState.style.display = 'none';
    if (prevBtn) prevBtn.style.display = 'flex';
    if (nextBtn) nextBtn.style.display = 'flex';

    // Reset to first image
    photoGalleryCurrentIndex = 0;

    // Display first image
    updatePhotoGalleryDisplay();

    // Build thumbnails
    buildPhotoGalleryThumbnails();
  }

  function updatePhotoGalleryDisplay() {
    var mainImage = document.getElementById('photo-gallery-main-image');
    var counterEl = document.getElementById('photo-gallery-counter');

    if (photoGalleryImages.length === 0) return;

    var currentImage = photoGalleryImages[photoGalleryCurrentIndex];

    if (mainImage && currentImage) {
      mainImage.src = currentImage.url;
      mainImage.alt = currentImage.alt;
    }

    if (counterEl) {
      counterEl.textContent = (photoGalleryCurrentIndex + 1) + ' / ' + photoGalleryImages.length;
    }

    // Update thumbnail active state
    var thumbnails = document.querySelectorAll('.photo-gallery-thumbnail');
    thumbnails.forEach(function(thumb, index) {
      if (index === photoGalleryCurrentIndex) {
        thumb.classList.add('active');
      } else {
        thumb.classList.remove('active');
      }
    });

    // Scroll active thumbnail into view (vertical scrolling)
    var activeThumb = document.querySelector('.photo-gallery-thumbnail.active');
    if (activeThumb) {
      activeThumb.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }

  function buildPhotoGalleryThumbnails() {
    var thumbnailsContainer = document.getElementById('photo-gallery-thumbnails');
    if (!thumbnailsContainer) return;

    thumbnailsContainer.innerHTML = '';

    photoGalleryImages.forEach(function(image, index) {
      var thumb = document.createElement('div');
      thumb.className = 'photo-gallery-thumbnail' + (index === photoGalleryCurrentIndex ? ' active' : '');
      thumb.onclick = function() { photoGalleryGoTo(index); };

      var img = document.createElement('img');
      img.src = image.url;
      img.alt = image.alt;

      thumb.appendChild(img);
      thumbnailsContainer.appendChild(thumb);
    });
  }

  window.photoGalleryNext = function(event) {
    if (event) event.stopPropagation();
    if (photoGalleryImages.length === 0) return;

    photoGalleryCurrentIndex = (photoGalleryCurrentIndex + 1) % photoGalleryImages.length;
    updatePhotoGalleryDisplay();
  };

  window.photoGalleryPrev = function(event) {
    if (event) event.stopPropagation();
    if (photoGalleryImages.length === 0) return;

    photoGalleryCurrentIndex = (photoGalleryCurrentIndex - 1 + photoGalleryImages.length) % photoGalleryImages.length;
    updatePhotoGalleryDisplay();
  };

  window.photoGalleryGoTo = function(index) {
    if (index < 0 || index >= photoGalleryImages.length) return;

    photoGalleryCurrentIndex = index;
    updatePhotoGalleryDisplay();
  };

  // Zoom functionality
  var photoGalleryZoomOpen = false;
  var zoomHistoryState = null;
  var mobileZoomLevel = 1;
  var mobileZoomStartDistance = 0;
  var mobileZoomStartLevel = 1;
  var mobilePanStartX = 0;
  var mobilePanStartY = 0;
  var mobileTranslateX = 0;
  var mobileTranslateY = 0;
  var mobileStartTranslateX = 0;
  var mobileStartTranslateY = 0;
  var isTwoFingerTouch = false;

  window.togglePhotoGalleryZoom = function(event) {
    if (event) event.stopPropagation();

    var zoomOverlay = document.getElementById('photo-gallery-zoom-overlay');
    var zoomedImage = document.getElementById('photo-gallery-zoomed-image');
    var mainImage = document.getElementById('photo-gallery-main-image');

    if (!zoomOverlay || !zoomedImage || !mainImage) return;

    if (photoGalleryImages.length === 0) return;

    // Set the zoomed image source
    zoomedImage.src = mainImage.src;
    zoomedImage.alt = mainImage.alt;

    // Reset zoom state
    mobileZoomLevel = 1;
    mobileTranslateX = 0;
    mobileTranslateY = 0;
    desktopZoomLevel = 1;
    zoomedImage.style.transform = 'scale(1) translate(0px, 0px)';

    // Show the overlay
    zoomOverlay.classList.add('active');
    photoGalleryZoomOpen = true;

    // Push history state for back button support
    zoomHistoryState = window.history.state;
    window.history.pushState({ photoGalleryZoomOpen: true }, '');
  };

  // Handle click on zoom overlay - only close if clicking the background
  window.handleZoomOverlayClick = function(event) {
    // Only close if the click target is the overlay itself, not the image
    if (event.target.id === 'photo-gallery-zoom-overlay') {
      closePhotoGalleryZoom(event);
    }
  };

  window.closePhotoGalleryZoom = function(event, fromPopstate) {
    if (event) event.stopPropagation();

    var zoomOverlay = document.getElementById('photo-gallery-zoom-overlay');
    if (!zoomOverlay) return;

    // Only modify history if not triggered by popstate
    if (!fromPopstate && photoGalleryZoomOpen) {
      window.history.back();
      return; // The popstate handler will call this function again with fromPopstate=true
    }

    zoomOverlay.classList.remove('active');
    photoGalleryZoomOpen = false;

    // Reset zoom level on the image
    var zoomedImage = document.getElementById('photo-gallery-zoomed-image');
    if (zoomedImage) {
      zoomedImage.style.transform = 'scale(1) translate(0px, 0px)';
      desktopZoomLevel = 1;
      mobileZoomLevel = 1;
      mobileTranslateX = 0;
      mobileTranslateY = 0;
    }
  };

  // Desktop zoom with ctrl+scroll
  var desktopZoomLevel = 1;
  var zoomOverlayEl = document.getElementById('photo-gallery-zoom-overlay');
  if (zoomOverlayEl) {
    zoomOverlayEl.addEventListener('wheel', function(e) {
      if (!photoGalleryZoomOpen) return;

      // Only zoom if ctrl/cmd is held
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault();

        var zoomedImage = document.getElementById('photo-gallery-zoomed-image');
        if (!zoomedImage) return;

        // Adjust zoom level
        var delta = e.deltaY > 0 ? -0.1 : 0.1;
        desktopZoomLevel = Math.max(0.5, Math.min(5, desktopZoomLevel + delta));

        zoomedImage.style.transform = 'scale(' + desktopZoomLevel + ')';
      }
    }, { passive: false });
  }

  // Desktop zoom with ctrl++ and ctrl+-
  document.addEventListener('keydown', function(e) {
    if (!photoGalleryZoomOpen) return;

    if ((e.ctrlKey || e.metaKey) && (e.key === '+' || e.key === '=' || e.key === '-' || e.key === '_')) {
      e.preventDefault();

      var zoomedImage = document.getElementById('photo-gallery-zoomed-image');
      if (!zoomedImage) return;

      // Adjust zoom level
      var delta = (e.key === '+' || e.key === '=') ? 0.25 : -0.25;
      desktopZoomLevel = Math.max(0.5, Math.min(5, desktopZoomLevel + delta));

      zoomedImage.style.transform = 'scale(' + desktopZoomLevel + ')';
    }
  });

  // Mobile pinch-to-zoom on zoomed image
  var zoomedImageEl = document.getElementById('photo-gallery-zoomed-image');
  var zoomOverlayForTouch = document.getElementById('photo-gallery-zoom-overlay');

  if (zoomedImageEl && zoomOverlayForTouch) {
    // Get distance between two touch points
    function getTouchDistance(touches) {
      var dx = touches[0].clientX - touches[1].clientX;
      var dy = touches[0].clientY - touches[1].clientY;
      return Math.sqrt(dx * dx + dy * dy);
    }

    // Update image transform
    function updateZoomedImageTransform() {
      var zoomedImage = document.getElementById('photo-gallery-zoomed-image');
      if (zoomedImage) {
        zoomedImage.style.transform = 'scale(' + mobileZoomLevel + ') translate(' + mobileTranslateX + 'px, ' + mobileTranslateY + 'px)';
      }
    }

    // Prevent clicks/taps on the image from closing the overlay
    zoomedImageEl.addEventListener('click', function(e) {
      e.stopPropagation();
    });

    // Track if user is actively touching/zooming
    var isActiveTouch = false;
    var touchStartTime = 0;

    zoomedImageEl.addEventListener('touchstart', function(e) {
      if (!photoGalleryZoomOpen) return;
      e.stopPropagation(); // Prevent overlay from receiving this

      isActiveTouch = true;
      touchStartTime = Date.now();

      if (e.touches.length === 2) {
        // Two finger touch - start pinch zoom
        e.preventDefault();
        isTwoFingerTouch = true;
        mobileZoomStartDistance = getTouchDistance(e.touches);
        mobileZoomStartLevel = mobileZoomLevel;
      } else if (e.touches.length === 1) {
        // Single finger touch - prepare for pan if zoomed
        mobilePanStartX = e.touches[0].clientX;
        mobilePanStartY = e.touches[0].clientY;
        mobileStartTranslateX = mobileTranslateX;
        mobileStartTranslateY = mobileTranslateY;
      }
    }, { passive: false });

    zoomedImageEl.addEventListener('touchmove', function(e) {
      if (!photoGalleryZoomOpen) return;
      e.stopPropagation();
      e.preventDefault(); // Prevent scrolling

      if (e.touches.length === 2) {
        // Pinch zoom
        if (!isTwoFingerTouch) {
          // Started as single touch, now two fingers
          isTwoFingerTouch = true;
          mobileZoomStartDistance = getTouchDistance(e.touches);
          mobileZoomStartLevel = mobileZoomLevel;
        }

        var currentDistance = getTouchDistance(e.touches);
        var scale = currentDistance / mobileZoomStartDistance;
        mobileZoomLevel = Math.max(1, Math.min(5, mobileZoomStartLevel * scale));

        // Reset translation if zoomed back to 1
        if (mobileZoomLevel <= 1.05) {
          mobileZoomLevel = 1;
          mobileTranslateX = 0;
          mobileTranslateY = 0;
        }

        updateZoomedImageTransform();
      } else if (e.touches.length === 1 && mobileZoomLevel > 1 && !isTwoFingerTouch) {
        // Pan while zoomed
        var dx = e.touches[0].clientX - mobilePanStartX;
        var dy = e.touches[0].clientY - mobilePanStartY;

        // Limit pan based on zoom level
        var maxPan = (mobileZoomLevel - 1) * 200;
        mobileTranslateX = Math.max(-maxPan, Math.min(maxPan, mobileStartTranslateX + dx / mobileZoomLevel));
        mobileTranslateY = Math.max(-maxPan, Math.min(maxPan, mobileStartTranslateY + dy / mobileZoomLevel));

        updateZoomedImageTransform();
      }
    }, { passive: false });

    zoomedImageEl.addEventListener('touchend', function(e) {
      e.stopPropagation();

      if (e.touches.length === 0) {
        isTwoFingerTouch = false;
        isActiveTouch = false;
      } else if (e.touches.length < 2) {
        isTwoFingerTouch = false;
      }
    });

    // Double tap to toggle zoom
    var lastTapTime = 0;
    var lastTapX = 0;
    var lastTapY = 0;

    zoomedImageEl.addEventListener('touchend', function(e) {
      if (!photoGalleryZoomOpen) return;
      if (e.touches.length > 0) return; // Still touching with other fingers

      var currentTime = Date.now();
      var tapLength = currentTime - lastTapTime;
      var touch = e.changedTouches[0];

      // Check if this is a double tap (within 300ms and within 50px of last tap)
      var dx = Math.abs(touch.clientX - lastTapX);
      var dy = Math.abs(touch.clientY - lastTapY);

      if (tapLength < 300 && tapLength > 0 && dx < 50 && dy < 50) {
        // Double tap detected
        e.preventDefault();
        if (mobileZoomLevel > 1.1) {
          // Reset zoom
          mobileZoomLevel = 1;
          mobileTranslateX = 0;
          mobileTranslateY = 0;
        } else {
          // Zoom in to 2.5x
          mobileZoomLevel = 2.5;
        }
        updateZoomedImageTransform();
        lastTapTime = 0; // Reset to prevent triple tap
      } else {
        lastTapTime = currentTime;
        lastTapX = touch.clientX;
        lastTapY = touch.clientY;
      }
    });

    // Prevent the overlay click handler from triggering on image touch
    zoomOverlayForTouch.addEventListener('touchstart', function(e) {
      // Only close if tap is directly on the overlay (background), not on the image
      if (e.target === zoomOverlayForTouch) {
        // This is a tap on the background - will close via click handler
      }
    });
  }

  // Handle keyboard navigation in gallery
  document.addEventListener('keydown', function(event) {
    // Handle zoom overlay first
    if (photoGalleryZoomOpen) {
      if (event.key === 'Escape') {
        closePhotoGalleryZoom();
      }
      return;
    }

    if (!photoGalleryLightboxOpen) return;

    if (event.key === 'ArrowLeft') {
      photoGalleryPrev();
    } else if (event.key === 'ArrowRight') {
      photoGalleryNext();
    } else if (event.key === 'Escape') {
      closePhotoGalleryLightbox();
    }
  });

  // Handle touch swipe in gallery
  var photoGalleryTouchStartX = 0;
  var galleryMainContainer = document.getElementById('photo-gallery-main-container');
  if (galleryMainContainer) {
    galleryMainContainer.addEventListener('touchstart', function(e) {
      photoGalleryTouchStartX = e.touches[0].clientX;
    });

    galleryMainContainer.addEventListener('touchend', function(e) {
      var touchEndX = e.changedTouches[0].clientX;
      var diffX = photoGalleryTouchStartX - touchEndX;

      if (Math.abs(diffX) > 50) {
        if (diffX > 0) {
          photoGalleryNext();
        } else {
          photoGalleryPrev();
        }
      }
    });
  }

  // Handle browser back button for gallery and zoom
  window.addEventListener('popstate', function(event) {
    // Check if zoom overlay is open first
    if (photoGalleryZoomOpen) {
      closePhotoGalleryZoom(null, true); // Pass true to indicate this is from popstate
      return;
    }
    // Then check if gallery is open
    if (photoGalleryLightboxOpen) {
      closePhotoGalleryLightbox();
    }
  });

  // ========================================
  // End Photo Gallery Functions
  // ========================================

  // ========================================
  // Auction Terms Lightbox Functions
  // ========================================
  var auctionTermsImages = [];
  var auctionTermsCurrentIndex = 0;
  var auctionTermsLightboxOpen = false;
  var auctionTermsPreviousHistoryState = null;

  window.openAuctionTermsLightbox = function() {
    var lightbox = document.getElementById('auction-terms-lightbox');
    if (!lightbox) return;

    lightbox.style.display = 'flex';
    auctionTermsLightboxOpen = true;
    auctionTermsPreviousHistoryState = window.history.state;
    window.history.pushState({ auctionTermsLightboxOpen: true }, '');

    // Load auction terms images
    loadAuctionTermsImages();
  };

  window.closeAuctionTermsLightbox = function() {
    var lightbox = document.getElementById('auction-terms-lightbox');
    if (!lightbox) return;

    lightbox.style.display = 'none';
    auctionTermsLightboxOpen = false;
    window.history.replaceState(auctionTermsPreviousHistoryState, '');
  };

  window.closeAuctionTermsLightboxOnOutsideClick = function(event) {
    if (event.target === document.getElementById('auction-terms-lightbox')) {
      closeAuctionTermsLightbox();
    }
  };

  // Load images from the auction-terms product
  function loadAuctionTermsImages() {
    var mainImage = document.getElementById('auction-terms-main-image');
    var loadingEl = document.getElementById('auction-terms-loading');
    var thumbnailsContainer = document.getElementById('auction-terms-thumbnails');
    var counterEl = document.getElementById('auction-terms-counter');

    // Show loading state
    if (loadingEl) loadingEl.style.display = 'flex';
    if (mainImage) mainImage.style.display = 'none';

    // Fetch auction terms product
    fetch('/products/auction-terms.json')
      .then(function(response) {
        if (!response.ok) throw new Error('Product not found');
        return response.json();
      })
      .then(function(data) {
        var product = data.product;
        if (product && product.images && product.images.length > 0) {
          auctionTermsImages = product.images.map(function(img, index) {
            return {
              url: img.src,
              alt: img.alt || 'Auction Terms Slide ' + (index + 1)
            };
          });

          auctionTermsCurrentIndex = 0;
          updateAuctionTermsDisplay();
          populateAuctionTermsThumbnails(thumbnailsContainer);

          if (loadingEl) loadingEl.style.display = 'none';
          if (mainImage) mainImage.style.display = 'block';
        } else {
          if (loadingEl) loadingEl.innerHTML = '<p>No auction terms images found</p>';
        }
      })
      .catch(function(error) {
        console.error('Failed to load auction terms:', error);
        if (loadingEl) loadingEl.innerHTML = '<p>Failed to load auction terms</p>';
      });
  }

  function updateAuctionTermsDisplay() {
    var mainImage = document.getElementById('auction-terms-main-image');
    var counterEl = document.getElementById('auction-terms-counter');

    if (auctionTermsImages.length === 0) return;

    var currentImage = auctionTermsImages[auctionTermsCurrentIndex];

    if (mainImage && currentImage) {
      mainImage.src = currentImage.url;
      mainImage.alt = currentImage.alt;
    }

    if (counterEl) {
      counterEl.textContent = (auctionTermsCurrentIndex + 1) + ' / ' + auctionTermsImages.length;
    }

    // Update thumbnails active state
    document.querySelectorAll('.auction-terms-thumbnail').forEach(function(thumb, index) {
      thumb.classList.toggle('active', index === auctionTermsCurrentIndex);
    });
  }

  function populateAuctionTermsThumbnails(container) {
    if (!container) return;
    container.innerHTML = '';

    auctionTermsImages.forEach(function(image, index) {
      var thumb = document.createElement('div');
      thumb.className = 'auction-terms-thumbnail' + (index === 0 ? ' active' : '');
      thumb.onclick = function() { auctionTermsGoTo(index); };

      var img = document.createElement('img');
      img.src = image.url;
      img.alt = image.alt;

      thumb.appendChild(img);
      container.appendChild(thumb);
    });
  }

  window.auctionTermsNext = function(event) {
    if (event) event.stopPropagation();
    if (auctionTermsImages.length === 0) return;

    auctionTermsCurrentIndex = (auctionTermsCurrentIndex + 1) % auctionTermsImages.length;
    updateAuctionTermsDisplay();
  };

  window.auctionTermsPrev = function(event) {
    if (event) event.stopPropagation();
    if (auctionTermsImages.length === 0) return;

    auctionTermsCurrentIndex = (auctionTermsCurrentIndex - 1 + auctionTermsImages.length) % auctionTermsImages.length;
    updateAuctionTermsDisplay();
  };

  window.auctionTermsGoTo = function(index) {
    if (index < 0 || index >= auctionTermsImages.length) return;

    auctionTermsCurrentIndex = index;
    updateAuctionTermsDisplay();
  };

  // Handle keyboard navigation for auction terms lightbox
  document.addEventListener('keydown', function(event) {
    if (!auctionTermsLightboxOpen) return;

    if (event.key === 'ArrowLeft') {
      auctionTermsPrev();
    } else if (event.key === 'ArrowRight') {
      auctionTermsNext();
    } else if (event.key === 'Escape') {
      closeAuctionTermsLightbox();
    }
  });

  // Handle touch swipe for auction terms lightbox
  var auctionTermsTouchStartX = 0;
  var auctionTermsMainContainer = document.getElementById('auction-terms-main-container');
  if (auctionTermsMainContainer) {
    auctionTermsMainContainer.addEventListener('touchstart', function(e) {
      auctionTermsTouchStartX = e.touches[0].clientX;
    });

    auctionTermsMainContainer.addEventListener('touchend', function(e) {
      var touchEndX = e.changedTouches[0].clientX;
      var diffX = auctionTermsTouchStartX - touchEndX;

      if (Math.abs(diffX) > 50) {
        if (diffX > 0) {
          auctionTermsNext();
        } else {
          auctionTermsPrev();
        }
      }
    });
  }

  // Handle browser back button for auction terms lightbox
  window.addEventListener('popstate', function(event) {
    if (auctionTermsLightboxOpen) {
      closeAuctionTermsLightbox();
    }
  });

  // ========================================
  // End Auction Terms Lightbox Functions
  // ========================================

  // Toggle product details (expand/collapse title and show/hide metafield info)
  window.toggleProductDetails = function(productId) {
    var collapsedTitle = document.getElementById('title-' + productId);
    var expandedTitle = document.getElementById('full-title-' + productId);
    var metafieldInfo = document.getElementById('metafield-' + productId);
    var productItem = collapsedTitle.closest('.product-list-item');

    if (collapsedTitle && expandedTitle && metafieldInfo) {
      var isExpanded = expandedTitle.style.display !== 'none';

      if (isExpanded) {
        // Collapse: Show truncated title, hide full title and metafield info
        collapsedTitle.style.display = '-webkit-box';
        expandedTitle.style.display = 'none';
        metafieldInfo.style.display = 'none';
        productItem.style.backgroundColor = '';
      } else {
        // Expand: Hide truncated title, show full title and metafield info
        collapsedTitle.style.display = 'none';
        expandedTitle.style.display = 'block';
        metafieldInfo.style.display = 'block';
        productItem.style.backgroundColor = 'rgba(var(--mhv-accent-rgb), 0.1)';
      }
    }
  };

  // Toggle lot request functionality
  window.toggleLotRequest = function(productId, productHandle) {
    // Canonical, non-empty identity (works for customers without a screen name)
    var screenName = MHV.requesterLabel(cust);
    if (cust && screenName) {
      var customerId = String(cust.id);
      var consignorNumber = cust.consignorNumber || '';
      var button = document.getElementById('request-btn-' + productId);

      if (!button || button.disabled) return;

      // Disable button during request
      button.disabled = true;
      button.style.opacity = '0.5';

      // Check current state via data attribute
      var isRequested = button.dataset.requested === 'true';
      var action = isRequested ? 'cancel' : 'request';

      // Abort controller for timeout
      var controller = new AbortController();
      var timeoutId = setTimeout(function() { controller.abort(); }, 10000);

      // Make API call to handle request
      var requestHeaders = { 'Content-Type': 'application/json' };
      if (window.MHV_CUSTOMER_KEY) requestHeaders['X-Customer-Key'] = window.MHV_CUSTOMER_KEY;
      fetch(MHV.apiUrl('/api/lot-request'), {
        method: 'POST',
        headers: requestHeaders,
        signal: controller.signal,
        body: JSON.stringify({
          productId: productId,
          productHandle: productHandle,
          customerId: customerId,
          screenName: screenName,
          consignorNumber: consignorNumber,
          action: action
        })
      })
      .then(function(response) { return response.json(); })
      .then(function(data) {
        if (data.success) {
          updateRequestButton(productId, data.requestData, screenName);
        } else {
          console.error('Lot request failed:', data.error);
          MHV.toast('Request failed. Please try again.', { type: 'error' });
        }
      })
      .catch(function(error) {
        if (error.name === 'AbortError') {
          console.error('Lot request timed out');
          MHV.toast('Request timed out. Please try again.', { type: 'error' });
        } else {
          console.error('Lot request error:', error);
          MHV.toast('Request failed. Please try again.', { type: 'error' });
        }
      })
      .finally(function() {
        clearTimeout(timeoutId);
        // Re-enable button
        button.disabled = false;
        button.style.opacity = '1';
      });
    } else {
      MHV.toast('Please log in with a registered bidder account to request lots.', { type: 'warning' });
    }
  };

  // Parse request metafield data
  function parseRequestMetafield(requestString) {
    if (!requestString) return null;

    var parts = requestString.split(';');
    if (parts.length < 3) return null;

    return {
      timestamp: parts[0],
      count: parseInt(parts[1]) || 0,
      requesters: parts[2] || ''
    };
  }

  // Update request button based on current request state
  function updateRequestButton(productId, requestData, currentScreenName) {
    var button = document.getElementById('request-btn-' + productId);
    if (!button) return;

    var lotLabel = button.dataset.lotLabel || 'Lot';

    if (requestData && requestData.requesters) {
      var requesters = requestData.requesters.split(',');
      var isRequested = requesters.includes(currentScreenName);
      var requestCount = parseInt(requestData.count) || 0;

      if (isRequested) {
        button.dataset.requested = 'true';
        button.textContent = 'Requested (' + requestCount + ')';
        button.style.background = 'rgba(var(--mhv-success-rgb), 0.2)';
        button.style.borderColor = 'var(--mhv-current-bid-match)';
      } else {
        button.dataset.requested = 'false';
        button.textContent = 'Request ' + lotLabel + ' (' + requestCount + ')';
        button.style.background = 'rgba(var(--mhv-accent-rgb), 0.2)';
        button.style.borderColor = 'var(--mhv-accent)';
      }
    } else {
      // No requests
      button.dataset.requested = 'false';
      button.textContent = 'Request ' + lotLabel;
      button.style.background = 'rgba(var(--mhv-accent-rgb), 0.2)';
      button.style.borderColor = 'var(--mhv-accent)';
    }
  }

  // Load products from collection
  window.loadProductList = function() {
    var loadingEl = document.getElementById('product-list-loading');
    var containerEl = document.getElementById('product-list-container');
    var errorEl = document.getElementById('product-list-error');
    var contentEl = document.getElementById('product-list-content');

    // Show loading, hide others
    loadingEl.style.display = 'block';
    containerEl.style.display = 'none';
    errorEl.style.display = 'none';

  // ---- DB truth over the metafield snapshot ----
  // The lot list renders Current Bid and the bid count out of the Shopify product
  // metafields carried by /api/collection-with-metafields. Those are a mirror, synced
  // best-effort after a bid, and they do not reflect LIVE bids at all — so a bidder
  // reading this list during a sale can see a price that has already moved, and quick
  // decisions get made on it. (Same fault that hid late absentee bids from the host
  // page on 20260820; the auction hub has corrected for it since, this list had not.)
  //
  // Patch the authoritative figures onto each product BEFORE rendering, so every
  // downstream reader of product.metafields gets the truth without knowing about it.
  // Best-effort: on any failure the metafield snapshot still renders, as before.
  function applyLiveBidTruth(products, done) {
    if (!products || !products.length || !liveSessionCode || !/^\d{8}$/.test(liveSessionCode)) {
      return done(products);
    }
    var finished = false;
    function finish() { if (!finished) { finished = true; done(products); } }
    // Never let the list hang on this — render regardless after a beat.
    setTimeout(finish, 2500);
    try {
      (window.mhvFetch || fetch)(MHV.apiUrl('/api/live-bids/' + liveSessionCode))
        .then(function (r) { return r.json(); })
        .then(function (data) {
          if (data && data.bids) {
            products.forEach(function (p) {
              var pid = String(p.productId || p.id || '').replace(/[^0-9]/g, '');
              var b = pid && data.bids[pid];
              if (!b || !p.metafields) return;
              if (b.currentBid != null) p.metafields['absentee_current_bid'] = String(b.currentBid);
              if (b.numberOfBids != null) p.metafields['absentee_number_of_bids'] = String(b.numberOfBids);
              if (b.highBidderSn) p.metafields['absentee_high_bidder_sn'] = b.highBidderSn;
              // The reserve badge from the same fresh bid (the feed's own reserve_status is
              // derived from the Shopify mirror, which a live floor bid never reaches).
              if (b.reserveStatus === 'met' || b.reserveStatus === 'not_met') p.metafields['reserve_status'] = b.reserveStatus;
            });
          }
          finish();
        })
        .catch(finish);
    } catch (e) { finish(); }
  }

    if (!liveSessionCode) {
      console.error('No auction date code available');
      showProductListError();
      return;
    }
    var collectionHandle = liveSessionCode;

    // Fetch products from enhanced API with metafields and caching
    fetch(MHV.apiUrl('/api/collection-with-metafields/' + collectionHandle))
      .then(function(response) {
        if (!response.ok) {
          throw new Error('HTTP ' + response.status);
        }
        return response.json();
      })
      .then(function(data) {
        // Check if collection was not found (graceful handling)
        if (data.collectionNotFound) {
          showNoCatalogMessage();
          return;
        }
        applyLiveBidTruth(data.products, function (products) {
          allProducts = products;
          displayProductList(products);
        });
      })
      .catch(function(error) {
        console.error('Error loading products:', error);
        showProductListError();
      });
  };

  // Preload catalog check on page load to determine button visibility
  // This runs silently in the background - doesn't affect the lightbox display
  window.checkCatalogAvailability = function() {
    if (CONFIG.auctionDateCode) {
      var collectionHandle = liveSessionCode;

      fetch(MHV.apiUrl('/api/collection-with-metafields/' + collectionHandle))
        .then(function(response) { return response.json(); })
        .then(function(data) {
          if (data.collectionNotFound || !data.products || data.products.length === 0) {
            // No catalog - button stays hidden
            return;
          }
          // Catalog found with products - show button only if unsold items exist
          applyLiveBidTruth(data.products, function (products) {
            allProducts = products; // Cache products for lightbox use
            updateRequestListButtonVisibility(allProducts);
          });
        })
        .catch(function(error) {
          // On error, button stays hidden
        });
    }
  };

  // Check catalog availability on page load
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', checkCatalogAvailability);
  } else {
    checkCatalogAvailability();
  }

  // Show a friendly message when no catalog exists for this session
  function showNoCatalogMessage() {
    var loadingEl = document.getElementById('product-list-loading');
    var containerEl = document.getElementById('product-list-container');
    var errorEl = document.getElementById('product-list-error');
    var contentEl = document.getElementById('product-list-content');

    if (loadingEl) loadingEl.style.display = 'none';
    if (containerEl) containerEl.style.display = 'block';
    if (errorEl) errorEl.style.display = 'none';

    if (contentEl) {
      contentEl.innerHTML = '<div style="text-align: center; padding: 30px; color: var(--mhv-text-subtle);"><div style="font-size: 24px; margin-bottom: 10px;">&#x1F4CB;</div><div style="font-size: 14px;">No Catalog Found</div></div>';
    }
  }

  function displayProductList(products) {
    var loadingEl = document.getElementById('product-list-loading');
    var containerEl = document.getElementById('product-list-container');
    var contentEl = document.getElementById('product-list-content');

    loadingEl.style.display = 'none';
    containerEl.style.display = 'block';

    if (!products || products.length === 0) {
      contentEl.innerHTML = '<div style="text-align: center; padding: 20px; color: var(--mhv-text-subtle);">No products found in this collection</div>';
      return;
    }

    // Show/hide the Request List button based on unsold items
    updateRequestListButtonVisibility(products);

    // Helper: Check if product is sold
    var isProductSold = function(product) {
      var firstVariant = product.variants && product.variants[0];
      var price = firstVariant ? firstVariant.price : 0;
      var tags = product.tags || [];
      var hasPassed = tags.some(function(tag) { return tag.toLowerCase().includes('passed'); });
      var isComingUpPrice = isPlaceholderPrice(price);
      return !isComingUpPrice || hasPassed;
    };

    // Update My Bids and My Favs tab visibility
    if (customerBidderNumber) {
      var myBidsTab = document.getElementById('my-bids-tab');
      var myFavsTab = document.getElementById('my-favs-tab');

      var bidsCount = products.filter(function(p) { return p.tags && p.tags.includes(customerBidderNumber); }).length;
      var favsCount = products.filter(function(p) { return p.tags && p.tags.includes(customerFavTag); }).length;

      if (myBidsTab) {
        myBidsTab.style.display = bidsCount > 0 ? 'block' : 'none';
        myBidsTab.textContent = bidsCount > 0 ? 'My Bids (' + bidsCount + ')' : 'My Bids';
      }
      if (myFavsTab) {
        myFavsTab.style.display = favsCount > 0 ? 'block' : 'none';
        myFavsTab.textContent = favsCount > 0 ? 'My Favs (' + favsCount + ')' : 'My Favs';
      }
    }

    // Filter products based on current tab
    var filteredProducts = products.filter(function(product) {
      var firstVariant = product.variants && product.variants[0];
      var price = firstVariant ? firstVariant.price : 0;
      var tags = product.tags || [];
      var hasPassed = tags.some(function(tag) { return tag.toLowerCase().includes('passed'); });
      var isComingUpPrice = isPlaceholderPrice(price);

      if (currentTab === 'coming-up') {
        // Coming up: price = 999999.99 (as string or number) and no "passed" tag
        return isComingUpPrice && !hasPassed;
      } else if (currentTab === 'my-bids') {
        // My Bids: products tagged with user's bidder number
        return customerBidderNumber && tags.includes(customerBidderNumber);
      } else if (currentTab === 'my-favs') {
        // My Favs: products tagged with user's bidder number + '-favs'
        return customerFavTag && tags.includes(customerFavTag);
      } else {
        // Sold: price != 999999.99 OR has "passed" tag
        return !isComingUpPrice || hasPassed;
      }
    });

    // Apply search filter (title only)
    if (liveProductSearchFilter && liveProductSearchFilter.trim()) {
      var searchTerm = liveProductSearchFilter.toLowerCase().trim();
      filteredProducts = filteredProducts.filter(function(product) {
        return (product.title || '').toLowerCase().includes(searchTerm);
      });
    }

    // "Coming Up" tab → request-count DESC with same-requester staggering (shared helper, matches
    // the host page). Other tabs → lot-number / title order.
    var sortedProducts;
    if (currentTab === 'coming-up') {
      sortedProducts = MHV.orderComingUpLots(filteredProducts);
    } else {
      sortedProducts = filteredProducts.sort(function(a, b) {
        var getLotNumber = function(product) {
          if (product.variants && product.variants[0] && product.variants[0].sku) {
            var skuMatch = product.variants[0].sku.match(/LOT(\d+)/);
            if (skuMatch) return parseInt(skuMatch[1]);
          }
          return 0;
        };
        var lotA = getLotNumber(a);
        var lotB = getLotNumber(b);
        if (lotA && lotB) return lotA - lotB;
        return a.title.localeCompare(b.title);
      });
    }

    // Consignment tag for current user
    var customerConsignmentTag = (cust && cust.consignorNumber) ? (cust.consignorNumber + '-consignment').toLowerCase() : null;

    // Generate product list HTML
    var productHTML = sortedProducts.map(function(product) {
      var firstVariant = product.variants && product.variants[0];
      var sku = firstVariant ? firstVariant.sku : '';
      var price = firstVariant ? firstVariant.price : 0;
      var tags = product.tags || [];
      var hasPassed = tags.some(function(tag) { return tag.toLowerCase().includes('passed'); });
      var image = product.images && product.images[0] ? product.images[0].src : '';
      var isSold = isProductSold(product);

      // Get buyer number and absentee high bidder from metafields for badge logic
      var buyerNumber = product.metafields ? (product.metafields['buyernumber'] || product.metafields['buyer_number'] || product.metafields['buyer-number']) : null;
      var absenteeHighBidder = product.metafields ? (product.metafields['absentee_high_bidder'] || product.metafields['absentee-high-bidder']) : null;
      var userBidOnThis = customerBidderNumber && tags.includes(customerBidderNumber);
      var userFavoritedThis = customerFavTag && tags.includes(customerFavTag);
      var userWonThis = customerBidderNumber && buyerNumber && buyerNumber.toString() === customerBidderNumber.toString();
      var userIsHighBidder = customerBidderNumber && absenteeHighBidder && absenteeHighBidder.toString() === customerBidderNumber.toString();

      // Style the first 7 characters if they match "Lot XXX" pattern
      var styledTitle = MHV.escapeHtml(product.title);
      if (product.title.length >= 7 && product.title.toLowerCase().startsWith('lot ')) {
        var lotPart = product.title.substring(0, 7); // "Lot XXX"
        var restOfTitle = product.title.substring(7);
        styledTitle = '<span style="color: var(--mhv-accent); font-weight: bold;">' + MHV.escapeHtml(lotPart) + '</span>' + MHV.escapeHtml(restOfTitle);
      }

      // Generate badge HTML based on tab and status
      var badgeHtml = '';
      if (isSold && !hasPassed) {
        // SOLD items badge logic
        if (currentTab === 'my-favs') {
          if (userWonThis) {
            badgeHtml = '<span style="background: var(--mhv-success-vivid); color: var(--mhv-text-primary); padding: 2px 8px; border-radius: 3px; font-size: 11px; font-weight: bold; margin-left: 8px;">You Won!</span>';
          } else if (userBidOnThis) {
            badgeHtml = '<span style="background: var(--mhv-error); color: var(--mhv-text-primary); padding: 2px 8px; border-radius: 3px; font-size: 11px; font-weight: bold; margin-left: 8px;">Outbid</span>';
          }
        } else if (currentTab === 'my-bids') {
          if (userWonThis) {
            badgeHtml = '<span style="background: var(--mhv-success-vivid); color: var(--mhv-text-primary); padding: 2px 8px; border-radius: 3px; font-size: 11px; font-weight: bold; margin-left: 8px;">You Won!</span>';
          } else {
            badgeHtml = '<span style="background: var(--mhv-error); color: var(--mhv-text-primary); padding: 2px 8px; border-radius: 3px; font-size: 11px; font-weight: bold; margin-left: 8px;">Outbid</span>';
          }
        } else if (currentTab === 'sold') {
          if (userWonThis) {
            badgeHtml = '<span style="background: var(--mhv-success-vivid); color: var(--mhv-text-primary); padding: 2px 8px; border-radius: 3px; font-size: 11px; font-weight: bold; margin-left: 8px;">You Won!</span>';
          } else if (userBidOnThis) {
            badgeHtml = '<span style="background: var(--mhv-error); color: var(--mhv-text-primary); padding: 2px 8px; border-radius: 3px; font-size: 11px; font-weight: bold; margin-left: 8px;">Outbid</span>';
          }
        }
      } else if (!isSold && (currentTab === 'my-bids' || currentTab === 'my-favs')) {
        // UNSOLD items badge logic for My Bids and My Favs tabs
        if (userBidOnThis) {
          if (userIsHighBidder) {
            badgeHtml = '<span style="background: var(--mhv-success-vivid); color: var(--mhv-text-primary); padding: 2px 8px; border-radius: 3px; font-size: 11px; font-weight: bold; margin-left: 8px;">Winning</span>';
          } else {
            badgeHtml = '<span style="background: var(--mhv-error); color: var(--mhv-text-primary); padding: 2px 8px; border-radius: 3px; font-size: 11px; font-weight: bold; margin-left: 8px;">Outbid</span>';
          }
        }
      }

      // Generate price/status display
      var priceDisplay = '';
      if (currentTab === 'coming-up' || ((currentTab === 'my-bids' || currentTab === 'my-favs') && !isSold)) {
        // Coming up tab or unsold items in bids/favs: no price shown
        priceDisplay = '';
      } else {
        // Sold tab or sold items in bids/favs: show price or "Lot Passed"
        if (hasPassed) {
          priceDisplay = '<div style="color: var(--mhv-error); font-size: 12px; font-weight: bold;">Lot Passed' + badgeHtml + '</div>';
        } else {
          // Shopify Collections API might return price as string or already formatted
          var priceAmount;
          if (typeof price === 'string') {
            priceAmount = parseFloat(price).toFixed(2);
          } else {
            // If it's a number in cents, divide by 100
            priceAmount = (price / 100).toFixed(2);
          }

          priceDisplay = '<div style="font-size: 12px; font-weight: bold;"><span style="color: var(--mhv-success);">Sold for:</span> $' + priceAmount + badgeHtml + '</div>';
        }
      }

      // Check for metafields data (current bid, bid count, favorites)
      var metafieldInfo = '';
      var alwaysVisibleInfo = '';

      // Show bid info for coming-up tab and unsold items in my-bids/my-favs
      var showBidInfo = currentTab === 'coming-up' || ((currentTab === 'my-bids' || currentTab === 'my-favs') && !isSold);
      if (showBidInfo && product.metafields) {
        var currentBid = product.metafields['absentee_current_bid'] || product.metafields['absentee-current-bid'] || product.metafields['current-bid'];
        var bidCount = parseInt(product.metafields['absentee_number_of_bids'] || product.metafields['absentee-number-of-bids'] || product.metafields['bid-count'] || product.metafields['number-of-bids'] || 0);
        var favCount = parseInt(product.metafields['fav_count'] || product.metafields['fav-count'] || product.metafields['favorite-count'] || 0);

        // Always visible current bid info (with badge for my-bids/my-favs)
        var unsoldBadge = (currentTab === 'my-bids' || currentTab === 'my-favs') ? badgeHtml : '';
        // Optional fields — minimum bid is only shown when no bids exist (per requirements);
        // reserve renders a met/not-met badge whenever the reserve_price metafield is set.
        var minBid = product.metafields ? (product.metafields['minimum_bid'] || product.metafields['minimum-bid']) : null;
        // ⚠️ THE AMOUNT NEVER REACHES THIS PAGE ANY MORE. The server strips reserve_price
        // from the public product (lib/product-view, 2026-09-14 — a bidder could read the
        // exact floor off the feed) and sends `reserve_status` — 'met' / 'not_met' — derived
        // from the same comparison this code used to make. A staff socket still gets the
        // amount, so the old arithmetic stays as the fallback when the status is absent.
        var reserveStatus = product.metafields ? product.metafields['reserve_status'] : null;
        var reserve = product.metafields ? (product.metafields['reserve_price'] || product.metafields['reserve-price']) : null;
        var minBidBadge = '';
        if (bidCount === 0 && minBid && parseFloat(minBid) > 0) {
          var mbAmt = parseFloat(minBid).toFixed(2);
          minBidBadge = ' <span style="display:inline-block;margin-left:4px;padding:1px 5px;border:1px solid var(--mhv-accent);border-radius:3px;font-size:.85em;font-weight:bold;color:var(--mhv-accent);" title="Bidding starts at this amount.">Opening Bid: $' + mbAmt + '</span>';
        }
        var reserveBadge = '';
        if (reserveStatus === 'met' || reserveStatus === 'not_met' || (reserve && parseFloat(reserve) > 0)) {
          var met;
          if (reserveStatus === 'met' || reserveStatus === 'not_met') {
            met = reserveStatus === 'met';
          } else {
            var resNum = parseFloat(reserve);
            var curNum = parseFloat(currentBid) || 0;
            met = curNum > 0 && curNum >= resNum;
          }
          var color = met ? 'var(--mhv-success)' : 'var(--mhv-warning-orange)';
          var tip = met ? "The current bid has reached the seller's reserve." : "Reserve hasn't been met yet — item may pass if bidding doesn't reach it.";
          reserveBadge = ' <span style="display:inline-block;margin-left:4px;padding:1px 5px;border:1px solid ' + color + ';border-radius:3px;font-size:.85em;font-weight:bold;color:' + color + ';" title="' + tip + '">' + (met ? 'Reserve Met' : 'Reserve Not Met') + '</span>';
        }
        if (bidCount > 0 && currentBid && parseFloat(currentBid) > 0) {
          var bidAmount = (parseFloat(currentBid)).toFixed(2);
          var formattedBid = '$' + bidAmount.replace(/\B(?=(\d{3})+(?!\d))/g, ','); // Add commas
          alwaysVisibleInfo = '<div style="font-size: 0.85em; margin-top: 4px; color: var(--mhv-text-primary);"><span style="font-weight: bold; color:var(--mhv-success);">Current Bid:</span> <span>' + formattedBid + '</span>' + unsoldBadge + reserveBadge + '</div>';
        } else {
          alwaysVisibleInfo = '<div style="font-size: 0.85em; margin-top: 4px; color: var(--mhv-text-primary);"><span>No bids</span>' + minBidBadge + reserveBadge + unsoldBadge + '</div>';
        }

        // Expandable detailed info (bid count, hot lot, favorites)
        var detailedParts = [];
        if (bidCount > 0) {
          detailedParts.push('<span style="font-weight:bold;">' + bidCount + ' Bid' + (bidCount > 1 ? 's' : '') + '</span>');

          // Add hot lot indicator if 5+ bids
          if (bidCount > 4) {
            detailedParts.push('<span style="font-weight:bold; color:var(--mhv-warning-orange);">&#x1F525;HotLot&#x1F525;</span>');
          }
        }

        // Add favorites count
        if (favCount > 0) {
          detailedParts.push('<span>' + ((window.MHV && MHV.countGlyph) ? MHV.countGlyph(favCount, 'heart') : (favCount + ' Fav' + (favCount > 1 ? 's' : ''))) + '</span>');
        }

        if (detailedParts.length > 0) {
          metafieldInfo = '<div style="font-size: 0.85em; margin-top: 4px; color: var(--mhv-text-primary);">' + detailedParts.join(' | ') + '</div>';
        } else {
          metafieldInfo = '';
        }
      }

      // Determine if we should show the request button (only for unsold items)
      // Also check if this is the user's own consignment
      var isOwnConsignment = customerConsignmentTag ? tags.some(function(tag) { return tag.toLowerCase() === customerConsignmentTag; }) : false;
      var showRequestButton = !isSold && !isOwnConsignment && (currentTab === 'coming-up' || currentTab === 'my-bids' || currentTab === 'my-favs');

      // Generate own consignment notice if applicable
      var ownConsignmentNotice = isOwnConsignment ? '\
        <div style="display: inline-block; background: rgba(var(--mhv-note-openbox-rgb), 0.15); border: 1px solid var(--mhv-warning-orange); border-radius: 4px; padding: 6px 10px; margin-top: 6px;">\
          <span style="font-size: 11px; color: var(--mhv-warning-orange); font-weight: bold;">&#x1F3F7;&#xFE0F; Your Consignment</span>\
        </div>\
      ' : '';

      // Lot label for request button
      var lotMatch = product.title.match(/^Lot \d+/);
      var lotLabel = lotMatch ? lotMatch[0] : 'Lot';

      // Request button HTML (only for logged-in users)
      var requestButtonHtml = '';
      if (cust && showRequestButton) {
        requestButtonHtml = '\
          <div style="margin-top: 8px;">\
            <button id="request-btn-' + product.id + '" data-requested="false" data-lot-label="' + lotLabel + '" data-act="toggle-lot-request" data-p0="' + MHV.escapeAttr(String(product.id)) + '" data-p1="' + MHV.escapeAttr(String(product.handle)) + '" style="background: rgba(var(--mhv-accent-rgb), 0.2); border: 1px solid var(--mhv-accent); color: var(--mhv-text-primary); padding: 6px 12px; border-radius: 4px; cursor: pointer; font-size: 12px; font-weight: bold;">\
              Request ' + lotLabel + '\
            </button>\
          </div>\
        ';
      }

      // Customer-only content (request button + consignment notice)
      var customerContent = cust ? (requestButtonHtml + ownConsignmentNotice) : '';

      return '\
        <div class="product-list-item" data-act="toggle-product-details" data-p0="' + MHV.escapeAttr(String(product.id)) + '" style="display: flex; align-items: flex-start; padding: 10px; border-bottom: 1px solid rgba(var(--mhv-accent-rgb), 0.2); cursor: pointer; transition: background-color 0.2s;">\
          ' + (image ? '<img src="' + image + '" alt="' + MHV.escapeAttr(product.title) + '" style="width: 60px; height: 60px; object-fit: cover; border-radius: 4px; margin-right: 15px; flex-shrink: 0;">' : '<div style="width: 60px; height: 60px; background: var(--mhv-btn-bg); border-radius: 4px; margin-right: 15px; flex-shrink: 0; display: flex; align-items: center; justify-content: center; font-size: 12px; color: var(--mhv-text-hint);">No Image</div>') + '\
          <div style="flex: 1; min-width: 0;">\
            <div id="title-' + product.id + '" class="product-title-collapsed" style="color: var(--mhv-text-primary); font-size: 13px; margin: 2px 0; line-height: 1.3; display: -webkit-box; -webkit-line-clamp: 3; -webkit-box-orient: vertical; overflow: hidden; text-overflow: ellipsis;">' + styledTitle + '</div>\
            <div id="full-title-' + product.id + '" class="product-title-expanded" style="display: none; color: var(--mhv-text-primary); font-size: 13px; margin: 2px 0; line-height: 1.3;">' + styledTitle + '</div>\
            ' + priceDisplay + '\
            ' + (showBidInfo ? alwaysVisibleInfo : '') + '\
            <div id="metafield-' + product.id + '" class="product-metafield-info" style="display: none;">\
              ' + metafieldInfo + '\
            </div>\
            ' + customerContent + '\
          </div>\
        </div>\
      ';
    }).join('');

    // Calculate total lots and current tab count
    var totalLots = products.length;
    var currentTabCount = sortedProducts.length;

    // Get appropriate empty state message
    var getEmptyMessage = function() {
      switch(currentTab) {
        case 'coming-up': return 'upcoming';
        case 'my-bids': return 'lots you\'ve bid on';
        case 'my-favs': return 'favorited';
        case 'sold': return 'sold';
        default: return '';
      }
    };

    if (sortedProducts.length === 0) {
      var emptyMessage = liveProductSearchFilter && liveProductSearchFilter.trim()
        ? 'No ' + getEmptyMessage() + ' lots match "' + MHV.escapeHtml(liveProductSearchFilter.trim()) + '"'
        : 'No ' + getEmptyMessage() + ' lots found';
      contentEl.innerHTML = '<div style="text-align: center; padding: 20px; color: var(--mhv-text-subtle);">' + emptyMessage + '</div>';
    } else {
      contentEl.innerHTML = '\
        <div id="product-list-content" style="max-height: 600px; overflow-y: auto;">\
          ' + productHTML + '\
        </div>\
      ';

      // Initialize request button states for logged-in users (for tabs with request buttons)
      var currentScreenName = MHV.requesterLabel(cust);
      if (cust && currentScreenName) {
        if (currentTab === 'coming-up' || currentTab === 'my-bids' || currentTab === 'my-favs') {
          sortedProducts.forEach(function(product) {
            // Only init buttons for unsold items
            var productIsSold = isProductSold(product);
            if (!productIsSold && product.metafields && product.metafields['requests']) {
              var requestData = parseRequestMetafield(product.metafields['requests']);
              updateRequestButton(product.id, requestData, currentScreenName);
            }
          });
        }
      }
    }

    // Calculate sold lots for progress bar
    var soldProducts = products.filter(function(product) {
      var firstVariant = product.variants && product.variants[0];
      var price = firstVariant ? firstVariant.price : 0;
      var tags = product.tags || [];
      var hasPassed = tags.some(function(tag) { return tag.toLowerCase().includes('passed'); });

      // A lot is considered "sold" if it doesn't have the coming-up price OR has a "passed" tag
      var isComingUpPrice = isPlaceholderPrice(price);
      return !isComingUpPrice || hasPassed;
    });

    var soldCount = soldProducts.length;
    var progressPercentage = totalLots > 0 ? Math.round((soldCount / totalLots) * 100) : 0;

    var remainingCount = totalLots - soldCount;

    // Update the footer with compact progress bar and close button (always runs)
    var footerEl = document.querySelector('#product-list-lightbox .lightbox-absentee-info-footer');
    if (footerEl) {
      footerEl.innerHTML = '\
        <div style="display: flex; justify-content: space-between; align-items: center; width: 100%;">\
          <!-- Compact Progress Bar Section -->\
          <div style="display: flex; flex-direction: column; align-items: flex-start; flex: 1; margin-right: 20px;">\
            <!-- Progress Bar Labels -->\
            <div style="display: flex; justify-content: space-between; width: 200px; margin-bottom: 3px;">\
              <span style="color: var(--mhv-text-subtle); font-size: 10px;">' + soldCount + ' lots sold</span>\
              <span style="color: var(--mhv-text-subtle); font-size: 10px;">' + remainingCount + ' lots remaining</span>\
            </div>\
            <!-- Progress Bar -->\
            <div style="width: 200px; height: 4px; background: var(--mhv-disabled-border) !important; border-radius: 2px; position: relative; overflow: hidden;">\
              <div style="width: ' + progressPercentage + '%; height: 100%; background: var(--mhv-accent) !important; border-radius: 2px; transition: width 0.3s ease; display: block !important;">&nbsp;</div>\
            </div>\
          </div>\
          \
          <!-- Close Button -->\
          <span class="lightbox-absentee-info-close-btn" onclick="closeProductListLightbox()">&#215; CLOSE</span>\
        </div>\
      ';
    }
  }

  function showProductListError() {
    var loadingEl = document.getElementById('product-list-loading');
    var containerEl = document.getElementById('product-list-container');
    var errorEl = document.getElementById('product-list-error');

    loadingEl.style.display = 'none';
    containerEl.style.display = 'none';
    errorEl.style.display = 'block';
  }


  // Listen for the popstate event to handle the Android back button
  window.addEventListener('popstate', function(event) {
    if (viewerLightboxOpen) {
      closeViewerLightbox();
    }
  });

  // ========================================
  // Slideshow Display Functions
  // ========================================

  // Slideshow function for displaying product images (using hub-banner-v3.liquid pattern)
  // disableAutoAdvance: if true, show first slide only and wait for manual navigation (used for auction terms)
  function showSlideshow(images, product, disableAutoAdvance) {
    // Only proceed if slideshow is supposed to be visible
    if (!window.isSlideshowVisible) {
      return;
    }

    // Slideshow is taking the stage — hide the video player entirely (it's covered
    // anyway, and an uncovered black box bled through on mobile). clearSlideshow /
    // toggleSlideshowVisibility(false) bring it back when the slideshow goes away.
    var videoContainer = document.getElementById('whep-player-container');
    if (videoContainer) {
      videoContainer.style.display = 'none';
    }

    // Show slideshow
    var slideshowContainer = document.getElementById('product-slideshow-container');
    if (slideshowContainer) {
      slideshowContainer.style.display = 'block';

      // Populate slideshow with images if available
      var slideshow = document.getElementById('product-slideshow');
      if (slideshow) {
        // Clear existing slides
        slideshow.innerHTML = '';

        // Clear any existing interval first
        if (window.slideshowInterval) {
          clearInterval(window.slideshowInterval);
          window.slideshowInterval = null;
        }

        if (images && images.length > 0) {
          // Create slides for each image
          images.forEach(function(image, index) {
            var slide = document.createElement('img');
            slide.src = image.url;
            slide.alt = image.alt || 'Product Image ' + (index + 1);
            slide.className = 'slide';
            slide.onerror = function() {
              if (!this.dataset.retried) {
                this.dataset.retried = '1';
                this.src = image.url + (image.url.indexOf('?') >= 0 ? '&' : '?') + 'r=1';
              }
            };
            slideshow.appendChild(slide);
          });

          // Show first slide — actual slide sync comes from owner via slideshow-slide-changed
          var slides = document.querySelectorAll('.photo-slideshow .slide');
          if (slides.length > 0) {
            slides[0].classList.add('active');
          }
          // Show the Gallery button since we have images (but not for auction terms)
          if (!disableAutoAdvance) {
            var galleryBtns = document.querySelectorAll('.photo-gallery-btn');
            galleryBtns.forEach(function(btn) { btn.classList.add('has-content'); });
          }
        } else {
          // No images - show placeholder or empty slideshow
          slideshow.innerHTML = '<div class="slideshow-placeholder"><div class="placeholder-content"><h3>Product Information</h3><p>No images available for this product</p></div></div>';

          // Hide the Gallery button since no images
          var galleryBtns = document.querySelectorAll('.photo-gallery-btn');
          galleryBtns.forEach(function(btn) { btn.classList.remove('has-content'); });
        }
      }
    }
  }
  window.showSlideshow = showSlideshow;

  // Auto-advance slideshow (matching hub-banner-v3.liquid functionality)
  function startSlideshowAutoAdvance() {
    // Reset cycle state for new slideshow
    window.hasCompletedCycle = false;

    var slides = document.querySelectorAll('.photo-slideshow .slide');

    if (slides.length > 0) {
      var currentSlide = 0;

      slides[currentSlide].classList.add('active');

      // Clear any existing interval
      if (window.slideshowInterval) {
        clearInterval(window.slideshowInterval);
      }

      // Start auto-advance
      window.slideshowInterval = setInterval(function() {
        slides[currentSlide].classList.remove('active');

        if (currentSlide < slides.length - 1) {
          currentSlide = currentSlide + 1;
        } else {
          // Reached the end - handle based on whether it's photo gallery or regular product
          if (window.isPhotoGallery) {
            // Photo gallery: continue looping
            currentSlide = 0;
          } else {
            // Regular product: complete one cycle then stop
            if (!window.hasCompletedCycle) {
              window.hasCompletedCycle = true;
              currentSlide = 0; // Go back to first slide
              clearInterval(window.slideshowInterval); // Stop auto-advance
              window.slideshowInterval = null;
            } else {
              // Manual navigation - allow looping
              currentSlide = 0;
            }
          }
        }

        slides[currentSlide].classList.add('active');
      }, 3000); // Change slide every 3 seconds (matching hub-banner-v3.liquid)

    } else {
      console.error('No slides found for auto-advance');
    }
  }

  // Clear slideshow function
  function clearSlideshow(fullClear) {
    // Stop any running slideshow autoplay
    if (window.slideshowInterval) {
      clearInterval(window.slideshowInterval);
      window.slideshowInterval = null;
    }

    // Hide slideshow container
    var slideshowContainer = document.getElementById('product-slideshow-container');
    if (slideshowContainer) {
      slideshowContainer.style.display = 'none';
    }

    // Show video container again with flexbox layout preserved
    var videoContainer = document.getElementById('whep-player-container');
    if (videoContainer) {
      videoContainer.style.display = 'flex';
    }

    if (fullClear) {
      // Full clear action (when empty lot submitted)
    }
  }
  window.clearSlideshow = clearSlideshow;

  // Toggle slideshow visibility function
  function toggleSlideshowVisibility(visible) {
    // Update global state
    window.isSlideshowVisible = visible;

    // Save to localStorage to keep both pages in sync
    localStorage.setItem('chatSlidesSlideshowVisible', visible.toString());

    // Show or hide slideshow container
    var slideshowContainer = document.getElementById('product-slideshow-container');
    if (slideshowContainer) {
      slideshowContainer.style.display = visible ? 'block' : 'none';
    }

    // Video player is the INVERSE of the slideshow: hidden while the slideshow covers
    // the stage (prevents the black video box from bleeding through, esp. on mobile),
    // shown again when the slideshow is toggled off.
    var videoContainer = document.getElementById('whep-player-container');
    if (videoContainer) {
      videoContainer.style.display = visible ? 'none' : 'flex';
    }

    // If turning slideshow on and we don't have images, treat it as a new lot submission
    if (visible && (!window.currentImages || window.currentImages.length === 0)) {
      // Emit the same event that the lot enter button emits
      if (typeof socket !== 'undefined' && socket) {
        socket.emit('submit-lot', { action: 'submit' });
      }
    }

    // If hiding slideshow, stop auto-advance and reset to first slide
    if (!visible) {
      // Stop auto-advance timer
      if (window.slideshowInterval) {
        clearInterval(window.slideshowInterval);
        window.slideshowInterval = null;
      }

      // Reset to first slide
      var slides = document.querySelectorAll('.photo-slideshow .slide');
      if (slides.length > 0) {
        slides.forEach(function(slide) { slide.classList.remove('active'); });
        slides[0].classList.add('active');

        // Reset slide index variables
        if (typeof window.currentSlide !== 'undefined') {
          window.currentSlide = 0;
        }
        if (typeof window.currentSlideIndex !== 'undefined') {
          window.currentSlideIndex = 0;
        }
      }
    }
  }
  window.toggleSlideshowVisibility = toggleSlideshowVisibility;

  // ========================================
  // Session Code Handlers
  // ========================================

  // Listen for session lock changes from host (replaces custom-session-code-update/reset)
  socket.on('session-lock-changed', function(data) {
    if (data.code && data.code !== liveSessionCode) {
      sessionStorage.setItem('mhv_live_session', data.code);
      liveSessionCode = data.code;
      updateExitLink(data.code);
      setTimeout(function() { location.reload(); }, 1000);
    }
  });

  // ========================================
  // GIVEAWAY WHEEL (Live Page Viewer Side)
  // ========================================

  var GIVEAWAY_WHEEL_COLORS = [
    '#FF00C2', '#8B00FF', '#00C8FF', '#00FF88', '#FFD700',
    '#FF4444', '#FF8800', '#44AAFF', '#AA44FF', '#44FF88',
    '#FF6699', '#6699FF', '#66FF99', '#FF9966', '#9966FF'
  ];

  // Compute proportional arc sizes from participant counts
  function computeLiveSegmentArcs(participants) {
    var total = 0;
    for (var i = 0; i < participants.length; i++) total += (participants[i].count || 1);
    if (total === 0) total = participants.length;
    var arcs = [];
    for (var j = 0; j < participants.length; j++) {
      arcs.push(((participants[j].count || 1) / total) * Math.PI * 2);
    }
    return arcs;
  }

  function drawLiveWheel(canvas, participants, highlightIndex, rotation) {
    var ctx = canvas.getContext('2d');
    var cx = canvas.width / 2;
    var cy = canvas.height / 2;
    var radius = Math.min(cx, cy) - 10;
    var numSegments = participants.length;
    var arcs = computeLiveSegmentArcs(participants);

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    var currentAngle = rotation;
    for (var i = 0; i < numSegments; i++) {
      var arcSize = arcs[i];
      var startAngle = currentAngle;
      var endAngle = currentAngle + arcSize;

      ctx.beginPath();
      ctx.moveTo(cx, cy);
      ctx.arc(cx, cy, radius, startAngle, endAngle);
      ctx.closePath();
      ctx.fillStyle = GIVEAWAY_WHEEL_COLORS[i % GIVEAWAY_WHEEL_COLORS.length];
      if (highlightIndex === i) {
        var x1 = cx + radius * Math.cos(startAngle);
        var y1 = cy + radius * Math.sin(startAngle);
        var x2 = cx + radius * Math.cos(endAngle);
        var y2 = cy + radius * Math.sin(endAngle);
        var grad = ctx.createLinearGradient(x1, y1, x2, y2);
        grad.addColorStop(0, '#FF0000');
        grad.addColorStop(0.17, '#FF8800');
        grad.addColorStop(0.33, '#FFE500');
        grad.addColorStop(0.5, '#00FF44');
        grad.addColorStop(0.67, '#00AAFF');
        grad.addColorStop(0.83, '#8800FF');
        grad.addColorStop(1, '#FF00CC');
        ctx.fillStyle = grad;
      }
      ctx.fill();
      if (highlightIndex === i) {
        ctx.strokeStyle = '#fff';
        ctx.lineWidth = 3;
        ctx.shadowColor = '#FFD700';
        ctx.shadowBlur = 15;
      } else {
        ctx.strokeStyle = '#000';
        ctx.lineWidth = 1;
      }
      ctx.stroke();
      ctx.shadowBlur = 0;

      // Text — only if segment is large enough
      if (arcSize > 0.15) {
        ctx.save();
        var textAngle = startAngle + arcSize / 2;
        ctx.translate(cx, cy);
        ctx.rotate(textAngle);
        ctx.textAlign = 'right';
        ctx.textBaseline = 'middle';
        ctx.fillStyle = '#fff';
        ctx.font = 'bold ' + (numSegments > 12 ? '12' : numSegments > 8 ? '14' : '16') + 'px sans-serif';
        var name = participants[i].screenName || participants[i];
        if (name.length > 14) name = name.substring(0, 12) + '..';
        ctx.fillText(name, radius - 10, 0);
        ctx.restore();
      }

      currentAngle = endAngle;
    }

    ctx.beginPath();
    ctx.arc(cx, cy, 22, 0, Math.PI * 2);
    ctx.fillStyle = '#1a1a1a';
    ctx.fill();
    ctx.strokeStyle = '#FF00C2';
    ctx.lineWidth = 3;
    ctx.stroke();
  }

  function animateLiveWheel(canvas, participants, winnerIndex, fullSpins, onComplete) {
    var arcs = computeLiveSegmentArcs(participants);

    // Compute angular center of winner's proportional segment
    var angleBeforeWinner = 0;
    for (var i = 0; i < winnerIndex; i++) angleBeforeWinner += arcs[i];
    var winnerCenter = angleBeforeWinner + arcs[winnerIndex] / 2;

    // We need: finalRotation + winnerCenter = -PI/2 (mod 2PI)
    // So: finalRotation = -PI/2 - winnerCenter - fullSpins * 2PI
    var targetRotation = -Math.PI / 2 - winnerCenter - fullSpins * Math.PI * 2;

    // Precompute cumulative boundary angles for ticker detection
    var boundaries = [0];
    for (var b = 0; b < arcs.length; b++) boundaries.push(boundaries[b] + arcs[b]);

    var startTime = null;
    var duration = 10000;

    function easeOutCubic(t) { return 1 - Math.pow(1 - t, 3); }

    function frame(timestamp) {
      if (!startTime) startTime = timestamp;
      var elapsed = timestamp - startTime;
      var progress = Math.min(elapsed / duration, 1);
      var easedProgress = easeOutCubic(progress);
      var currentRotation = targetRotation * easedProgress;

      var highlight = progress >= 1 ? winnerIndex : -1;
      drawLiveWheel(canvas, participants, highlight, currentRotation);

      // Determine which segment is under the ticker
      var topAngle = (-currentRotation - Math.PI / 2) % (Math.PI * 2);
      if (topAngle < 0) topAngle += Math.PI * 2;

      var segIdx = 0;
      for (var s = 0; s < boundaries.length - 1; s++) {
        if (topAngle < boundaries[s + 1]) { segIdx = s; break; }
      }

      // Ticker bounce: detect proximity to any segment boundary
      var ticker = document.querySelector('.giveaway-live-ticker');
      if (ticker && progress < 1) {
        var nearBoundary = false;
        for (var k = 0; k < boundaries.length; k++) {
          var dist = Math.abs(topAngle - boundaries[k]);
          if (dist > Math.PI) dist = Math.PI * 2 - dist;
          if (dist < 0.06) { nearBoundary = true; break; }
        }
        if (nearBoundary) ticker.classList.add('tick');
        else ticker.classList.remove('tick');
      } else if (ticker) {
        ticker.classList.remove('tick');
      }

      // Update name display above wheel
      var nameEl = document.getElementById('giveaway-live-name');
      if (nameEl) {
        var segName = participants[segIdx].screenName;
        if (nameEl.textContent !== segName) {
          nameEl.textContent = segName;
          nameEl.style.color = GIVEAWAY_WHEEL_COLORS[segIdx % GIVEAWAY_WHEEL_COLORS.length];
          nameEl.classList.remove('giveaway-name-flash');
          void nameEl.offsetWidth;
          nameEl.classList.add('giveaway-name-flash');
        }
      }

      if (progress < 1) {
        requestAnimationFrame(frame);
      } else {
        // Hide heading and name to make room for winner display below wheel
        var headingEl = document.getElementById('giveaway-live-heading');
        if (headingEl) headingEl.style.display = 'none';
        if (nameEl) nameEl.style.display = 'none';
        if (onComplete) onComplete();
      }
    }

    requestAnimationFrame(frame);
  }

  // Listen for giveaway spin broadcast
  var giveawayOverlayParent = null; // Track original parent for restore
  var giveawaySpinning = false;     // True while wheel is animating
  var giveawayPendingWinner = null; // Queued winner data if broadcast arrives mid-spin
  var giveawayDismissTimer = null;  // Auto-dismiss timer reference

  function showGiveawayWinner(data) {
    var overlay = document.getElementById('giveaway-wheel-overlay');
    var winnerEl = document.getElementById('giveaway-live-winner');
    if (!winnerEl) return;

    // Build winner display
    var html = '<div class="giveaway-live-winner-card">';
    html += '<div class="giveaway-live-winner-avatar">';
    if (data.profileImage) {
      html += '<img src="' + MHV.escapeAttr(data.profileImage) + '" alt="" onerror="this.style.display=\'none\';this.nextElementSibling.style.display=\'flex\';">';
      html += '<div class="giveaway-live-avatar-fallback" style="display:none;">' + MHV.escapeHtml((data.screenName || '?').substring(0, 2).toUpperCase()) + '</div>';
    } else {
      html += '<div class="giveaway-live-avatar-fallback">' + MHV.escapeHtml((data.screenName || '?').substring(0, 2).toUpperCase()) + '</div>';
    }
    html += '</div>';
    var gwName = (MHV.chatDisplayName
      ? MHV.chatDisplayName({ screenName: data.screenName, firstName: data.firstName, lastName: data.lastName, bidderNumber: data.bidderNumber, hideName: data.hideName, isAdminView: false }).primary
      : (data.displayName || data.screenName)) || 'Winner';
    html += '<div class="giveaway-live-winner-name">' + MHV.escapeHtml(gwName) + '</div>';
    html += '<div class="giveaway-live-winner-prize">WINS $' + (data.amount || 0) + ' OFF!</div>';
    html += '</div>';

    winnerEl.innerHTML = html;
    winnerEl.style.display = 'flex';

    // Trigger celebration (reuse sold celebration if available)
    if (typeof createSoldCelebration === 'function') {
      createSoldCelebration();
    }

    // Auto-dismiss after 8 seconds (from when winner is actually shown)
    if (giveawayDismissTimer) clearTimeout(giveawayDismissTimer);
    giveawayDismissTimer = setTimeout(function() {
      if (overlay) {
        overlay.style.display = 'none';
        // Restore overlay to original parent if it was moved
        if (giveawayOverlayParent && overlay.parentElement === document.body) {
          giveawayOverlayParent.appendChild(overlay);
          giveawayOverlayParent = null;
        }
      }
      if (winnerEl) winnerEl.style.display = 'none';
      giveawayDismissTimer = null;
    }, 8000);
  }

  socket.on('giveaway-spin-broadcast', function(data) {
    console.log('🎰 Giveaway spin received');
    var overlay = document.getElementById('giveaway-wheel-overlay');
    var canvas = document.getElementById('giveaway-live-canvas');
    var winnerEl = document.getElementById('giveaway-live-winner');
    if (!overlay || !canvas) return;

    var participants = data.participants || [];
    if (participants.length === 0) return;

    // Clear any pending dismiss timer from a previous giveaway
    if (giveawayDismissTimer) { clearTimeout(giveawayDismissTimer); giveawayDismissTimer = null; }
    giveawayPendingWinner = null;
    giveawaySpinning = true;

    // On mobile, move overlay to body to escape stacking context
    if (document.documentElement.classList.contains('mobile-device') && overlay.parentElement !== document.body) {
      giveawayOverlayParent = overlay.parentElement;
      document.body.appendChild(overlay);
    }

    // Show overlay
    overlay.style.display = 'flex';
    if (winnerEl) winnerEl.style.display = 'none';

    // Insert heading + name into .giveaway-wheel-inner (above the canvas-wrap)
    var wheelInner = canvas.closest('.giveaway-wheel-inner');
    var canvasWrap = canvas.closest('.giveaway-canvas-wrap');

    var headingEl = document.getElementById('giveaway-live-heading');
    if (!headingEl) {
      headingEl = document.createElement('div');
      headingEl.id = 'giveaway-live-heading';
      headingEl.className = 'giveaway-live-heading';
      wheelInner.insertBefore(headingEl, canvasWrap);
    }
    headingEl.innerHTML = '<h2>\uD83C\uDF81 Giveaway</h2><div class="giveaway-live-subtitle">for $' + (data.amount || 0) + ' OFF</div>';
    headingEl.style.display = '';

    var nameEl = document.getElementById('giveaway-live-name');
    if (!nameEl) {
      nameEl = document.createElement('div');
      nameEl.id = 'giveaway-live-name';
      nameEl.className = 'giveaway-wheel-name';
      wheelInner.insertBefore(nameEl, canvasWrap);
    }
    nameEl.textContent = '';
    nameEl.style.display = '';
    nameEl.classList.remove('giveaway-name-winner');

    // Draw initial wheel (proportional segments based on count)
    drawLiveWheel(canvas, participants, -1, 0);

    // Animate (use fullSpins from host so all clients spin identically)
    animateLiveWheel(canvas, participants, data.winnerIndex || 0, data.fullSpins || 10, function() {
      giveawaySpinning = false;
      // If winner broadcast arrived while spinning, show it now
      if (giveawayPendingWinner) {
        showGiveawayWinner(giveawayPendingWinner);
        giveawayPendingWinner = null;
      }
    });
  });

  // Listen for giveaway winner broadcast
  socket.on('giveaway-winner-broadcast', function(data) {
    console.log('🎉 Giveaway winner:', data.screenName, '$' + data.amount);
    if (giveawaySpinning) {
      // Wheel still spinning — queue winner data, show after animation completes
      giveawayPendingWinner = data;
      return;
    }
    showGiveawayWinner(data);
  });

  // ========================================
  // Socket Error Handlers
  // ========================================

  // Handle socket errors
  socket.on('error', function(error) {
    console.error('Socket error:', error);
  });

  socket.on('disconnect', function(reason) {
    console.log('Disconnected from server:', reason);
    setLiveConnected(false);
  });

  socket.on('reconnect', function(attemptNumber) {
    console.log('Reconnected to server after', attemptNumber, 'attempts');
    socket.emit('request-visibility-state');
  });

  socket.on('connect_error', function(error) {
    console.log('Connection error, retrying...', error.message);
  });

  // ========================================
  // FULLSCREEN TOGGLE for video area
  // ========================================

  // Fullscreen toggle for castr-interface
  var liveFsBtn = document.getElementById('live-fullscreen-btn');
  var castrInterface = document.querySelector('.castr-interface');
  function toggleFullscreen() {
    if (!castrInterface) return;
    if (!document.fullscreenElement && !document.webkitFullscreenElement) {
      if (castrInterface.requestFullscreen) {
        castrInterface.requestFullscreen();
      } else if (castrInterface.webkitRequestFullscreen) {
        castrInterface.webkitRequestFullscreen();
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen();
      }
    }
  }

  if (liveFsBtn && castrInterface) {
    liveFsBtn.addEventListener('click', toggleFullscreen);
    castrInterface.addEventListener('dblclick', toggleFullscreen);

    // Update icon on fullscreen change
    function onLiveFsChange() {
      var isFs = !!document.fullscreenElement || !!document.webkitFullscreenElement;
      var svg = liveFsBtn.querySelector('svg path');
      if (svg) {
        if (isFs) {
          svg.setAttribute('d', 'M5 16h3v3h2v-5H5v2zm3-8H5v2h5V5H8v3zm6 11h2v-3h3v-2h-5v5zm2-11V5h-2v5h5V8h-3z');
        } else {
          svg.setAttribute('d', 'M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z');
        }
      }
    }
    document.addEventListener('fullscreenchange', onLiveFsChange);
    document.addEventListener('webkitfullscreenchange', onLiveFsChange);
  }

  // ========================================
  // Delegated click handling (SEC-61 / CSP)
  // Replaces interpolated inline onclick handlers on dynamically-built HTML.
  // Registered ONCE (module IIFE runs once). e.target.closest('[data-act]')
  // resolves to the nearest actionable element, preserving the prior
  // stopPropagation behavior (a nested action fires alone, not its ancestor's).
  // ========================================
  document.addEventListener('click', function(e) {
    var t = e.target.closest('[data-act]');
    if (!t) return;
    switch (t.getAttribute('data-act')) {
      case 'bin-list-purchase':
        e.stopPropagation();
        window.binListPurchase(t.getAttribute('data-p0'), Number(t.getAttribute('data-p1')), t);
        break;
      case 'scroll-to-bin-item':
        window.scrollToBinItem(t.getAttribute('data-p0'));
        break;
      case 'toggle-lot-request':
        e.stopPropagation();
        window.toggleLotRequest(t.getAttribute('data-p0'), t.getAttribute('data-p1'));
        break;
      case 'toggle-product-details':
        window.toggleProductDetails(t.getAttribute('data-p0'));
        break;
    }
  });

})();
